/* eslint-disable @typescript-eslint/no-non-null-assertion */

import fastq, { queueAsPromised } from 'fastq';
import React from 'react';
import { DateRange, delay, guid, isNullOrUndefined, LQ, TimestampRange, UTC } from 'rewire-common';
import { createWatcherFn, freeze, observable, WatcherTypeFn } from 'rewire-core';
import {
  cloneValue,
  createColumn,
  createColumnsToggleMenuItems,
  createGrid,
  ICell,
  ICellCustomValue,
  IColumn,
  IGrid,
  IGridOptions,
  IRow,
  IRowData,
} from 'rewire-grid';
import { IToggleMenuItem } from 'rewire-ui';
import {
  DeepPartial,
  IEmployee,
  IGenerator,
  IGroup,
  IJob,
  IOccupation,
  IReason,
  ISavedSchedule,
  ISchedule,
  IScheduleGridOptions,
  IShift,
  ISubscriptionBatchedRecord,
  ITemplate,
  ITimecard,
  ITimecardTemplate,
  JobInfoInput,
  JobInput,
  MessageFilterInput,
  OperationType,
  SaveTimecardInput,
  ScheduleFilterInput,
  ScheduleMode,
  TimecardInput,
} from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import { employeeScheduleRouteManager, jobScheduleRouteManager, PlanRouteManagerBase } from '../main-navigation/RouteManager';
import messageCountsPollingService, { MessageCountsPollingModel } from '../services/MessageCountsPollingService';
import messagesSubscriptionService, { MessagesSubscriptionModel } from '../services/MessagesSubscriptionService';
import timecardsBatchedCallbackSubscriptionService, {
  TimecardsBatchedCallbackSubscriptionModel,
} from '../services/TimecardsBatchedCallbackSubscriptionService';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import PagingModel from '../settings/PagingModel';
import { confirmationModel } from '../ui/ConfirmationDialog';
import mainDateRangeNavModel from '../ui/MainDateRangeNavModel';
import { notifications } from '../ui/Notifications';
import {
  getGapDetails,
  hasDetailsDifferentFromTemplate,
  roundTo,
  timecardTemplateToTimecard,
  timecardToTimecardInput,
  timestampRangeToNumericTime,
} from '../utility/DataConverters';
import { formatTime, YMDFormat } from '../utility/DateUtils';
import { nameObjCompare, nameObjMap } from '../utility/gridUtils';
import { objectKeys } from '../utility/Object';
import SelectedCellWatcher from '../utility/SelectedCellWatcher';
import { capitalize } from '../utility/StringUtils';
import duplicateJobDialogModel from './DuplicateJobDialogModel';
import duplicateJobsDialogModel from './DuplicateJobsDialogModel';
import { IDuplicateShiftData } from './DuplicateShiftDialogModel';
import infoPanelShiftViewModel from './InfoPanelShiftViewModel';
import {
  ScheduleEmployeeCell,
  ScheduleGridReorderableCellRenderer,
  ScheduleJobCell,
  ScheduleShiftCell,
  ScheduleShiftColumnHeader,
} from './ScheduleCells';
import scheduleControlsModel from './ScheduleControlsModel';
import scheduleInfoPanelModel from './ScheduleInfoPanelModel';
import { getUdfDisplayValue } from './UdfsModel';

export function timecardIntersects(details: ITimecard[], timecard: ITimecard) {
  const compatibleReasons = ['worked', 'break'];
  return details.some(
    (d) =>
      timecard.id !== d.id &&
      ((compatibleReasons.includes(timecard.reason?.category ?? '') && compatibleReasons.includes(d.reason?.category ?? '')) ||
        (timecard.reason?.category === 'absence' && d.reason?.category === 'absence')) &&
      timecard.effective &&
      d.effective &&
      d.effective.intersects(timecard.effective),
  );
}

export const savedScheduleReturnSchema = `
  id
  schedule {
    earnedDate
    template {
      id
      name
      template {
        start
        end
        units
        rate
        factor
        amount
        accountCode
        reason {
          id
          name
          category
        }
        overtimeReason {
          id
          name
        }
      }
    }
    details {
      id
      earnedDate
      effective
      accountCode
      code
      units
      factor
      rate
      amount
      shift
      createdBy
      createdDate
      timeOffStatus
      template {
        id
        name
        template {
          start
          end
          units
          rate
          factor
          amount
          accountCode
          reason {
            id
            name
            category
          }
          overtimeReason {
            id
            name
          }
        }
      }
      job {
        id
        effective
        cycleOffset
        fields
        generator {
          cycle {
            id
          }
        }
        group {
          id
          name
        }
        occupation {
          id
          name
        }
        employee {
          id
          name
        }
      }
      group {
        id
        name
        fields
        area {
          id
          name
        }
      }
      tags
      fields
      occupation {
        id
        name
      }
      employee {
        id
        name
      }
      reason {
        id
        name
        category
      }
      overtimeReason {
        id
        name
      }
    }
  }
`;

export const jobReturnSchema = `
  id
  cycleOffset
  position
  effective
  fields
  group {
    id
    name
    area {
      id
      name
    }
  }
  generator {
    id
    name
    cycle {
      id
    }
  }
  occupation {
    id
    name
  }
  employee {
    id
    name
    fields
  }
`;

const scheduleReturnSchema = `
  id
  cycleOffset
  position
  effective
  fields
  group {
    id
    name
    area {
      id
      name
    }
  }
  generator {
    id
    name
    cycle {
      id
    }
  }
  occupation {
    id
    name
  }
  employee {
    id
    name
    fields
  }
  schedule {
    earnedDate
    template {
      id
      name
      template {
        start
        end
        units
        rate
        factor
        amount
        accountCode
        reason {
          id
          name
          category
        }
        overtimeReason {
          id
          name
        }
      }
    }
    details {
      id
      earnedDate
      effective
      accountCode
      code
      units
      factor
      rate
      amount
      shift
      createdBy
      createdDate
      timeOffStatus
      template {
        id
        name
        template {
          start
          end
          units
          rate
          factor
          amount
          accountCode
          reason {
            id
            name
            category
          }
          overtimeReason {
            id
            name
          }
        }
      }
      job {
        id
        effective
        cycleOffset
        fields
        generator {
          cycle {
            id
          }
        }
        group {
          id
          name
        }
        occupation {
          id
          name
        }
        employee {
          id
          name
        }
      }
      group {
        id
        name
        fields
        area {
          id
          name
        }
      }
      tags
      fields
      occupation {
        id
        name
      }
      employee {
        id
        name
      }
      reason {
        id
        name
        category
      }
      overtimeReason {
        id
        name
      }
    }
  }
`;

export function cloneShift(shift: IShift) {
  let newDetails: ITimecard[] | undefined;
  if (shift.details) {
    newDetails = [];
    for (const detail of shift.details) {
      const newDetail: Partial<ITimecard> = {};
      for (const key of Object.keys(detail) as (keyof ITimecard)[]) {
        newDetail[key] = cloneValue(detail[key]);
      }

      newDetails.push(newDetail as ITimecard);
    }
  }

  const newShift: IShift = {
    earnedDate: cloneValue(shift.earnedDate),
    template: cloneValue(shift.template),
    details: newDetails,
  };
  return newShift;
}

export interface ITimeEntryText {
  employee: string;
  template: string;
  occupation: string;
  effective?: string;
  units: string;
  reason?: string;
}

export interface ITimeEntry {
  jobId: string;
  earnedDate: UTC;
  details: ITimecard[];
  effective?: TimestampRange;
  units: number;
  employee?: IEmployee;
  template?: ITemplate;
  occupation?: IOccupation;
  reason?: IReason;
  text: ITimeEntryText;
  timeOffStatus?: string;
}

export interface IShiftEntry {
  shift?: number;
  template?: ITemplate;
  templateText: string;
  details: ITimecard[];
  timeEntries: ITimeEntry[];
}

interface ITimeEntryDisplayOptions {
  showTemplates: boolean;
  showOccupations: boolean;
  showTimes: boolean;
  showReasons: boolean;
  showBreaks: boolean;
  showTimeOffRequests: boolean;
  showExceptionsOnly: boolean;
}

interface ITimeEntryGroupedDetails {
  groupedDetails: { [s: string]: ITimecard[] };
  nonAbsenceGroupedDetails: { [s: string]: ITimecard[] };
  absenceGroupedDetails: { [s: string]: ITimecard[] };
}

interface ITimeEntryBreakDetails {
  breakDetailsForNonAbsences: ITimecard[];
  breakDetailsForAbsences: ITimecard[];
}

export class ScheduleShiftCellData implements IShift, ICellCustomValue {
  jobId: string;
  earnedDate: UTC;
  template?: ITemplate;
  occupationId: string;
  details?: ITimecard[];
  shiftEntries!: IShiftEntry[];
  allShiftsTimeEntries!: ITimeEntry[];

  constructor(jobId: string, occupationId: string, shift: IShift) {
    ({ earnedDate: this.earnedDate, template: this.template, details: this.details } = shift);
    this.jobId = jobId;
    this.occupationId = occupationId;
    this.updateDisplay();
  }

  clone() {
    const newShift = cloneShift({ earnedDate: this.earnedDate, template: this.template, details: this.details });
    return new ScheduleShiftCellData(this.jobId, this.occupationId, newShift);
  }

  toString(): string {
    const shiftEntriesStr = this.shiftEntries.map((shiftEntry: IShiftEntry, _idx: number) => {
      const templateStr = shiftEntry.templateText ? `${shiftEntry.templateText}\n\n` : '';
      const timeEntriesStr = shiftEntry.timeEntries.map((timeEntry: ITimeEntry, _idx: number) => {
        const timeEntryEmployee = timeEntry.text.employee || '------------';
        const timeEntryTemplate = timeEntry.text.template ? `\n${timeEntry.text.template}` : '';
        const timeEntryOccupation = timeEntry.text.occupation ? `\n${timeEntry.text.occupation}` : '';
        const timeEntryDetails = this.timeEntryTextToDetails(timeEntry.text);
        return `${timeEntryEmployee}${timeEntryTemplate}${timeEntryOccupation}${timeEntryDetails ? `\n${timeEntryDetails}` : ''}`;
      });
      return `${templateStr}${timeEntriesStr?.length ? timeEntriesStr.join('\n\n') : ''}`;
    });

    return `${shiftEntriesStr?.length ? shiftEntriesStr.join('\n\n************************\n\n') : ''}`;
  }

  timeEntryTextToDetails(text: ITimeEntryText): string {
    return `${text.effective ? `${text.effective} - ` : ''}${text.units}${text.reason ? ` - ${text.reason}` : ''}`;
  }

  updateDisplay() {
    this.shiftEntries = this.getShiftEntries();
    this.allShiftsTimeEntries = this.shiftEntries.reduce((prev: ITimeEntry[], curr: IShiftEntry) => {
      prev.push(...curr.timeEntries);
      return prev;
    }, []);
  }

  getShiftEntries(): IShiftEntry[] {
    const shiftEntries = [] as IShiftEntry[];
    const detailsByShift = {} as { [s: string]: ITimecard[] };
    if (this.details) {
      for (const detail of this.details) {
        const shiftKey =
          scheduleModel.viewMode === 'Job' ? `shift-${detail.template?.id}-${detail.shift ?? -1}` : `shift-${detail.shift ?? -1}`;
        if (detailsByShift[shiftKey]) {
          detailsByShift[shiftKey].push(detail);
        } else {
          detailsByShift[shiftKey] = [detail];
        }
      }
    }

    for (const shiftKey of Object.keys(detailsByShift)) {
      const shiftDetails = detailsByShift[shiftKey];
      const shiftTemplate = shiftDetails[0].template;
      let shiftTemplateText =
        ((scheduleModel.viewMode === 'Employee' || scheduleModel.gridOptions.shiftCellTemplate) &&
          shiftTemplate &&
          shiftTemplate.name) ||
        '';
      const shiftTimeEntries = this.getTimeEntries(shiftDetails, shiftTemplate);
      if (
        scheduleModel.viewMode === 'Employee' &&
        (!scheduleModel.gridOptions.shiftCellExceptionsOnly || shiftTimeEntries.some((te) => !!te.occupation))
      ) {
        // don't show shift template in employee mode if not in exceptions only view with no exceptions
        shiftTemplateText = '';
      }
      const newShiftEntry: IShiftEntry = {
        shift: shiftDetails[0].shift,
        template: shiftTemplate,
        templateText: shiftTemplateText,
        details: shiftDetails,
        timeEntries: shiftTimeEntries,
      };
      shiftEntries.push(newShiftEntry);
    }

    this.sortShiftEntries(shiftEntries);
    return shiftEntries;
  }

  sortShiftEntries(shiftEntries: IShiftEntry[]) {
    shiftEntries.sort((shiftEntry1: IShiftEntry, shiftEntry2: IShiftEntry) => {
      const shiftNum1 = shiftEntry1.shift;
      const shiftNum2 = shiftEntry2.shift;
      const hasShiftNum1 = !isNullOrUndefined(shiftNum1);
      const hasShiftNum2 = !isNullOrUndefined(shiftNum2);
      const sortResult =
        hasShiftNum1 && !hasShiftNum2
          ? -1
          : !hasShiftNum1 && hasShiftNum2
            ? 1
            : shiftNum1! < shiftNum2!
              ? -1
              : shiftNum1! > shiftNum2!
                ? 1
                : 0;

      return sortResult;
    });
  }

  _getExceptionsOnlyTimeEntries(details: ITimecard[], template?: ITemplate) {
    const timeEntries = [] as ITimeEntry[];
    const employeeDetailsMap = new Map<string, ITimecard[]>();
    const showTimeOffRequests = scheduleModel.gridOptions.shiftCellTimeOffRequests;
    for (const detail of details) {
      const employeeId = detail.employee?.id ?? 'vacant';
      let detailsArr = employeeDetailsMap.get(employeeId);
      if (!detailsArr) {
        employeeDetailsMap.set(employeeId, [] as ITimecard[]);
        detailsArr = employeeDetailsMap.get(employeeId);
      }
      detailsArr?.push(detail);
    }

    for (const [_employeeId, dtls] of employeeDetailsMap) {
      if (
        hasDetailsDifferentFromTemplate(dtls, template?.template, this.earnedDate) ||
        (scheduleModel.viewMode === 'Employee' &&
          dtls.some((d) => d.template?.id !== this.template?.id || d.occupation?.id !== this.occupationId))
      ) {
        continue;
      }
      // add basic time entries for employees with details that are identical to the shift template
      const employee = dtls[0].employee;
      const timeEntryText: ITimeEntryText = {
        employee: employee?.name ?? '',
        template: '',
        occupation: '',
        effective: '',
        units: '',
        reason: '',
      };
      const timeEntry: ITimeEntry = {
        jobId: this.jobId,
        earnedDate: this.earnedDate,
        details: dtls,
        effective: undefined,
        units: 0,
        employee: employee,
        template: undefined,
        occupation: undefined,
        reason: undefined,
        text: timeEntryText,
        timeOffStatus: showTimeOffRequests ? dtls[0].timeOffStatus : undefined,
      };
      timeEntries.push(timeEntry);
    }

    return timeEntries;
  }

  getTimeEntries(details: ITimecard[], template?: ITemplate): ITimeEntry[] {
    let _details = details.slice();
    const showTemplates = scheduleModel.viewMode === 'Employee' && scheduleModel.gridOptions.shiftCellTemplate;
    const showOccupations = scheduleModel.viewMode === 'Employee' && scheduleModel.gridOptions.shiftCellOccupations;
    const showTimes = scheduleModel.gridOptions.shiftCellTimes;
    const showReasons = scheduleModel.gridOptions.shiftCellReasons;
    const showBreaks = scheduleModel.gridOptions.shiftCellBreaks;
    const showTimeOffRequests = scheduleModel.gridOptions.shiftCellTimeOffRequests;
    const showExceptionsOnly = scheduleModel.gridOptions.shiftCellExceptionsOnly;
    const collapseBreaks = !showReasons || !showBreaks;
    const displayOptions: ITimeEntryDisplayOptions = {
      showTemplates,
      showOccupations,
      showTimes,
      showReasons,
      showBreaks,
      showExceptionsOnly,
      showTimeOffRequests,
    };

    const timeEntries = [] as ITimeEntry[];

    if (showExceptionsOnly) {
      const exceptionsOnlyTimeEntries = this._getExceptionsOnlyTimeEntries(details, template)!;
      const exceptionsOnlyTimeEntriesDetails = exceptionsOnlyTimeEntries.flatMap((te) => te.details);
      timeEntries.push(...exceptionsOnlyTimeEntries);
      // remove details already handled by exceptionsOnly method.
      _details = _details.filter((d: ITimecard) => !exceptionsOnlyTimeEntriesDetails.some((dtl: ITimecard) => d.id === dtl.id));
    }

    const groupedDetailsObj = this._getGroupedDetailsObj(_details, displayOptions)!;
    const { groupedDetails, absenceGroupedDetails } = groupedDetailsObj;

    if (collapseBreaks) {
      const breakDetailsForNonAbsences = _details.filter(this._isBreakDetail) || [];
      const breakDetailsForAbsences = details.filter(this._isBreakDetail) || [];
      const breakDetailsObj = { breakDetailsForNonAbsences, breakDetailsForAbsences };
      this._handleBreaks(groupedDetailsObj, breakDetailsObj, displayOptions);
    }

    const groupedTimeEntries = Object.keys(groupedDetails).map((key: string) => {
      const detailsGroup = groupedDetails[key];
      const isAbsenceGroup = Object.keys(absenceGroupedDetails).some((agdKey) => agdKey === key);
      const { contiguousDetailsGroups, contiguousAbsenceDetailsGroups } = this._getContiguousDetailsGroupsObj(
        detailsGroup,
        isAbsenceGroup,
        displayOptions,
      );

      const timeEntries = contiguousDetailsGroups.map((contiguousDetailsGroup: ITimecard[]) => {
        return this._getTimeEntry(contiguousDetailsGroup, displayOptions);
      });

      // remove break details from absence time entries.
      for (const details of contiguousAbsenceDetailsGroups) {
        for (let i = details.length - 1; i >= 0; i--) {
          const detail = details[i];
          if (this._isBreakDetail(detail)) {
            details.splice(i, 1);
          }
        }
      }

      return timeEntries;
    });

    const flattenedGroupedTimeEntries = groupedTimeEntries.flat();
    this.sortTimeEntries(flattenedGroupedTimeEntries, showReasons);
    timeEntries.push(...flattenedGroupedTimeEntries);
    return timeEntries;
  }

  _getTimeEntry(contiguousDetailsGroup: ITimecard[], displayOptions: ITimeEntryDisplayOptions) {
    const { showTemplates, showOccupations, showReasons, showBreaks, showTimes } = displayOptions;
    let start: UTC | undefined;
    let end: UTC | undefined;
    let units = 0;
    let timeOffStatus: string | undefined;
    const employee = contiguousDetailsGroup[0].employee;
    const timecardTemplate = contiguousDetailsGroup[0].template;
    const occupation = contiguousDetailsGroup[0].occupation;
    const reason = contiguousDetailsGroup.reduce((prev: ITimecard, curr: ITimecard) =>
      !prev.reason || this._isBreakDetail(prev) ? curr : prev,
    ).reason;

    for (const detail of contiguousDetailsGroup) {
      const detailStart = detail.effective?.start;
      const detailEnd = detail.effective?.end;
      const detailUnits = detail.units;
      const detailReason = detail.reason;
      if (showTimes && detailStart && (start === undefined || detailStart.date < start.date)) {
        start = detailStart;
      }
      if (showTimes && detailEnd && (end === undefined || detailEnd.date > end.date)) {
        end = detailEnd;
      }
      if (detailUnits && (!detailReason || showBreaks || !this._isBreakDetail(detail))) {
        units += detailUnits;
      }
      if (displayOptions.showTimeOffRequests && !timeOffStatus) {
        timeOffStatus = detail.timeOffStatus;
      }
    }

    const effective = start && end && !Number.isNaN(start) && !Number.isNaN(end) ? new TimestampRange(start, end) : undefined;

    const timeEntryText: ITimeEntryText = {
      employee: employee?.name ?? '',
      template: (showTemplates && timecardTemplate?.name) || '',
      occupation: (showOccupations && occupation?.name) || '',
      effective: (effective?.start && effective?.end && `${formatTime(effective.start)}-${formatTime(effective.end)}`) || '',
      units: String(roundTo(units, 2)),
      reason: (showReasons && reason?.name) || '',
    };

    const timeEntry: ITimeEntry = {
      jobId: this.jobId,
      earnedDate: this.earnedDate,
      details: contiguousDetailsGroup,
      effective: effective,
      units: units,
      employee: employee,
      template: timecardTemplate,
      occupation: occupation,
      reason: reason,
      text: timeEntryText,
      timeOffStatus,
    };
    return timeEntry;
  }

  _isBreakDetail(detail: ITimecard) {
    return detail.reason?.category === 'break';
  }

  _getGroupedDetailsObj(details: ITimecard[], displayOptions: ITimeEntryDisplayOptions) {
    const { showTemplates, showOccupations, showReasons, showBreaks } = displayOptions;
    const groupedDetails = {} as { [s: string]: ITimecard[] };
    const nonAbsenceGroupedDetails = {} as { [s: string]: ITimecard[] };
    const absenceGroupedDetails = {} as { [s: string]: ITimecard[] };
    for (const detail of details) {
      if ((!showReasons || !showBreaks) && this._isBreakDetail(detail)) {
        // breaks to be handled elsewhere so that they can potentially be collapsed
        continue;
      }
      const employeeKey = detail.employee?.id ?? 'vacant';
      const templateKey = (showTemplates && detail.template?.id) || 'noTemplate';
      const occupationKey = (showOccupations && detail.occupation?.id) || 'noOccupation';
      const isAbsence = detail.reason?.category === 'absence';
      const reasonKey = ((showReasons || isAbsence) && detail.reason?.id) || 'noReason';
      const key = `${employeeKey}::${templateKey}::${occupationKey}::${reasonKey}`;
      if (!groupedDetails[key]) {
        groupedDetails[key] = [];
        if (isAbsence) {
          absenceGroupedDetails[key] = groupedDetails[key];
        } else {
          nonAbsenceGroupedDetails[key] = groupedDetails[key];
        }
      }
      groupedDetails[key].push(detail);
    }

    return { groupedDetails, nonAbsenceGroupedDetails, absenceGroupedDetails } as ITimeEntryGroupedDetails;
  }

  _handleBreaks(
    timeEntryGroupedDetails: ITimeEntryGroupedDetails,
    timeEntryBreakDetails: ITimeEntryBreakDetails,
    displayOptions: ITimeEntryDisplayOptions,
  ) {
    const { groupedDetails, nonAbsenceGroupedDetails, absenceGroupedDetails } = timeEntryGroupedDetails;
    const { breakDetailsForNonAbsences, breakDetailsForAbsences } = timeEntryBreakDetails;
    const { showTemplates, showOccupations, showTimes, showReasons } = displayOptions;
    if (!breakDetailsForNonAbsences.length && !breakDetailsForAbsences.length) {
      return;
    }
    const getBreakPlacementIndex = (details: ITimecard[], breakDetail: ITimecard): number | undefined => {
      let placementIndex: number = 0;
      for (let j = 0; j < details.length; j++) {
        const detail = details[j];
        if (showTimes && ((!detail.effective && breakDetail.effective) || (detail.effective && !breakDetail.effective))) {
          continue;
        }
        if (!showTimes || (!detail.effective && !breakDetail.effective)) {
          if (detail.reason?.category === 'absence') {
            continue;
          }
          placementIndex = j + 1;
          break;
        }
        if (detail.effective!.start.equals(breakDetail.effective!.end)) {
          placementIndex = j;
          break;
        }
        if (detail.effective!.end.equals(breakDetail.effective!.start)) {
          placementIndex = j + 1;
          break;
        }
      }
      return placementIndex;
    };

    for (const breakDetail of breakDetailsForNonAbsences) {
      // add breaks to groupedDetails if same employee and contiguous
      // attempt to find a grouping of details for the same employee as this break detail, and if found, ensure that this break detail
      // is pushed on the grouping. This will allow the break to be collapsed.
      const employeeKey = breakDetail.employee?.id ?? 'vacant';
      const templateKey = (showTemplates && breakDetail.template?.id) || 'noTemplate';
      const occupationKey = (showOccupations && breakDetail.occupation?.id) || 'noOccupation';
      let reasonKey = (showReasons && breakDetail.reason?.id) || 'noReason';
      let placementIndex: number | undefined;
      const regExp = new RegExp(`^(${employeeKey})::(${templateKey})::(${occupationKey})::(.+)`);
      const nonAbsenceGroupedDetailsKeys = Object.keys(nonAbsenceGroupedDetails);
      for (let i = 0; i < nonAbsenceGroupedDetailsKeys.length; i++) {
        const nonAbsenceGroupedDetailsKey = nonAbsenceGroupedDetailsKeys[i];
        const employeeMatch = regExp[Symbol.match](nonAbsenceGroupedDetailsKey);
        if (!employeeMatch) {
          continue;
        }
        const detailsForKey = nonAbsenceGroupedDetails[nonAbsenceGroupedDetailsKey];
        placementIndex = getBreakPlacementIndex(detailsForKey, breakDetail);
        if (!isNullOrUndefined(placementIndex)) {
          reasonKey = employeeMatch[4];
          break;
        }
      }
      const key = `${employeeKey}::${templateKey}::${occupationKey}::${reasonKey}`;
      if (!groupedDetails[key]) {
        groupedDetails[key] = [];
        nonAbsenceGroupedDetails[key] = groupedDetails[key];
      }
      if (!isNullOrUndefined(placementIndex)) {
        nonAbsenceGroupedDetails[key].splice(placementIndex, 0, breakDetail);
      } else {
        nonAbsenceGroupedDetails[key].push(breakDetail);
      }
    }

    if (breakDetailsForAbsences.length) {
      // add breaks to every absence grouped details if contiguous
      for (const gdKey of Object.keys(absenceGroupedDetails)) {
        let placementIndex: number | undefined;
        const details = groupedDetails[gdKey];
        for (let i = 0; i < breakDetailsForAbsences.length; i++) {
          const breakDetail = breakDetailsForAbsences[i];
          placementIndex = getBreakPlacementIndex(details, breakDetail);

          if (!isNullOrUndefined(placementIndex)) {
            details.splice(placementIndex, 0, breakDetail);
          }
        }
      }
    }
  }

  _getContiguousDetailsGroupsObj(detailsGroup: ITimecard[], isAbsenceGroup: boolean, displayOptions: ITimeEntryDisplayOptions) {
    const { showTimes } = displayOptions;
    const contiguousDetailsGroups = [] as ITimecard[][];
    const contiguousAbsenceDetailsGroups = [] as ITimecard[][];
    let detailsContiguousGroup = [] as ITimecard[];
    for (const detail of detailsGroup) {
      const groupLength = detailsContiguousGroup.length;
      const detailStart = detail.effective?.start;
      const lastGroupDetailEndDate = groupLength && detailsContiguousGroup[groupLength - 1].effective?.end;
      if (
        groupLength &&
        showTimes &&
        ((!lastGroupDetailEndDate && detailStart) ||
          (lastGroupDetailEndDate && !detailStart) ||
          (lastGroupDetailEndDate && detailStart && !lastGroupDetailEndDate.equals(detailStart)))
      ) {
        contiguousDetailsGroups.push(detailsContiguousGroup);
        if (isAbsenceGroup) {
          // store groups that are absences so that the break details can be removed after calculating timecard entries.
          contiguousAbsenceDetailsGroups.push(detailsContiguousGroup);
        }
        detailsContiguousGroup = [] as ITimecard[];
      }
      detailsContiguousGroup.push(detail);
    }

    if (detailsContiguousGroup.length) {
      contiguousDetailsGroups.push(detailsContiguousGroup);
      if (isAbsenceGroup) {
        // store groups that are absences so that the break details can be removed after calculating timecard entries.
        contiguousAbsenceDetailsGroups.push(detailsContiguousGroup);
      }
    }

    return { contiguousDetailsGroups, contiguousAbsenceDetailsGroups };
  }

  sortTimeEntries(timeEntries: ITimeEntry[], showReasons: boolean) {
    timeEntries.sort((timeEntry1: ITimeEntry, timeEntry2: ITimeEntry) => {
      const isAbs1 = timeEntry1.reason?.category === 'absence';
      const isAbs2 = timeEntry2.reason?.category === 'absence';
      let sortResult = !isAbs1 && isAbs2 ? -1 : isAbs1 && !isAbs2 ? 1 : 0;

      if (sortResult === 0) {
        const startTime1 = timeEntry1.effective?.start?.date;
        const startTime2 = timeEntry2.effective?.start?.date;
        const hasStartTime1 = !isNullOrUndefined(startTime1);
        const hasStartTime2 = !isNullOrUndefined(startTime2);
        sortResult =
          !hasStartTime1 && hasStartTime2
            ? -1
            : hasStartTime1 && !hasStartTime2
              ? 1
              : !hasStartTime1 && !hasStartTime2
                ? 0
                : startTime1! < startTime2!
                  ? -1
                  : startTime1! > startTime2!
                    ? 1
                    : 0;
      }
      if (sortResult === 0) {
        const endTime1 = timeEntry1.effective?.end?.date;
        const endTime2 = timeEntry2.effective?.end?.date;
        const hasEndTime1 = !isNullOrUndefined(endTime1);
        const hasEndTime2 = !isNullOrUndefined(endTime2);
        sortResult =
          !hasEndTime1 && hasEndTime2
            ? -1
            : hasEndTime1 && !hasEndTime2
              ? 1
              : !hasEndTime1 && !hasEndTime2
                ? 0
                : endTime1! < endTime2!
                  ? -1
                  : endTime1! > endTime2!
                    ? 1
                    : 0;
      }
      if (showReasons && sortResult === 0) {
        const reason1 = timeEntry1.reason;
        const reason2 = timeEntry2.reason;
        const hasReason1 = !isNullOrUndefined(reason1);
        const hasReason2 = !isNullOrUndefined(reason2);
        sortResult =
          !hasReason1 && hasReason2
            ? -1
            : hasReason1 && !hasReason2
              ? 1
              : !hasReason1 && !hasReason2
                ? 0
                : reason1!.id.localeCompare(reason2!.id);
      }
      if (sortResult === 0) {
        const units1 = timeEntry1.units;
        const units2 = timeEntry2.units;
        sortResult = units1 < units2 ? -1 : units1 > units2 ? 1 : 0;
      }
      if (sortResult === 0) {
        const timecardId1 = timeEntry1.details[0]?.id ?? '';
        const timecardId2 = timeEntry2.details[0]?.id ?? '';
        sortResult = timecardId1.localeCompare(timecardId2);
      }
      return sortResult;
    });
  }

  static compare(val1?: ScheduleShiftCellData, val2?: ScheduleShiftCellData) {
    if (!val1 && !val2) return 0;
    if (!val1) return -1;
    if (!val2) return 1;
    return 0;
  }
}

export interface IScheduleJobCellDataText {
  occupation: string;
  generator?: string;
  effectiveStart: string;
  effectiveEnd: string;
}

export class ScheduleJobCellData implements IJob, ICellCustomValue {
  id: string;
  group: IGroup;
  generator?: IGenerator;
  occupation: IOccupation;
  employee?: IEmployee;
  cycleOffset?: number;
  effective: DateRange;
  position: number;
  fields?: Record<string, unknown>;
  text!: IScheduleJobCellDataText;

  constructor(job: IJob) {
    ({
      id: this.id,
      group: this.group,
      generator: this.generator,
      occupation: this.occupation,
      employee: this.employee,
      cycleOffset: this.cycleOffset,
      effective: this.effective,
      position: this.position,
      fields: this.fields,
    } = job);
    this.updateDisplay();
  }

  updateDisplay() {
    const text: IScheduleJobCellDataText = {
      occupation: this.occupation.name,
      generator: (scheduleModel.gridOptions.jobCellGenerator && this.generator?.name) || '',
      effectiveStart: this.effective.startAsString(),
      effectiveEnd: this.effective.endAsString() || '-',
    };
    this.text = text;
  }

  clone() {
    const newJob: IJob = {
      id: guid(),
      group: cloneValue(this.group),
      generator: cloneValue(this.generator),
      occupation: cloneValue(this.occupation),
      employee: cloneValue(this.employee),
      cycleOffset: this.cycleOffset,
      effective: cloneValue(this.effective),
      position: this.position + 1, // not necessarily correct, but not used at the moment
      fields: cloneValue(this.fields),
    };
    return new ScheduleJobCellData(newJob);
  }

  toString(): string {
    return `${this.text.occupation}\n${this.text.generator ? `${this.text.generator}\n` : ''}${this.text.effectiveStart} to ${
      this.text.effectiveEnd
    }`;
  }

  static compare(val1?: ScheduleJobCellData, val2?: ScheduleJobCellData) {
    if (!val1 && !val2) return 0;
    if (!val1) return -1;
    if (!val2) return 1;
    // TEMP
    if (!val1.occupation && !val2.occupation) return 0;
    if (!val1.occupation) return -1;
    if (!val2.occupation) return 1;
    return val1.occupation.name.localeCompare(val2.occupation.name);
  }
}

export interface EmployeeModeJobInfo {
  employeeId: string;
  jobId: string;
  dates: UTC[];
}

export type ScheduleCellType = 'Employee' | 'Job' | 'Shift';

interface IScheduleModelState {
  schedules: DeepPartial<ISchedule>[];
  grid: IGrid;
  gridOptions: IScheduleGridOptions;
  isLoading: boolean;
  selectedCell?: ICell;
  viewMode: ScheduleMode;
  hasAutoScheduleRules: boolean;
}

export class ScheduleModel {
  state: IScheduleModelState;
  pagingModel: PagingModel;
  messagesSubscriptionModel: MessagesSubscriptionModel;
  messageCountsPollingModel: MessageCountsPollingModel;
  timecardsBatchedCallbackSubscriptionModel: TimecardsBatchedCallbackSubscriptionModel;
  watchColumnVisibility: WatcherTypeFn;
  selectedCellWatcher: SelectedCellWatcher = new SelectedCellWatcher();
  updateGridShiftsQueue: queueAsPromised<ISubscriptionBatchedRecord<ITimecard>[]>;

  constructor() {
    this.state = observable({
      schedules: [] as DeepPartial<ISchedule>[],
      grid: {} as IGrid,
      gridOptions: {} as IScheduleGridOptions,
      isLoading: false,
      viewMode: 'Job',
      hasAutoScheduleRules: false,
    });

    this.updateGridShiftsQueue = fastq.promise(this, this.updateGridShifts, 1);

    const scheduleValidationMessagesSchema = `
      id
      text
      level
      type
      effective
      employee {
        id
        name
      }
      jobId
    `;
    const scheduleValidationMessagesFilter: MessageFilterInput = {
      types: ['validation:assignment', 'validation:shift', 'user', 'timeoff'],
    };

    const scheduleTimecardsSchema = `
      id
      earnedDate
      effective
      accountCode
      code
      units
      factor
      rate
      amount
      shift
      createdBy
      createdDate
      template {
        id
        name
        template {
          start
          end
          units
          rate
          factor
          amount
          accountCode
          reason {
            id
            name
            category
          }
          overtimeReason {
            id
            name
          }
        }
      }
      job {
        id
        effective
        cycleOffset
        fields
        generator {
          cycle {
            id
          }
        }
        group {
          id
          name
        }
        occupation {
          id
          name
        }
        employee {
          id
          name
        }
      }
      group {
        id
        name
        area {
          id
          name
        }
      }
      tags
      fields
      occupation {
        id
        name
      }
      employee {
        id
        name
      }
      reason {
        id
        name
        category
      }
      overtimeReason {
        id
        name
      }
    `;

    this.messagesSubscriptionModel = messagesSubscriptionService.createSubscriptionModel({
      returnSchema: scheduleValidationMessagesSchema,
      filter: scheduleValidationMessagesFilter,
    });
    this.messageCountsPollingModel = messageCountsPollingService.createPollingModel({ filter: scheduleValidationMessagesFilter });
    this.timecardsBatchedCallbackSubscriptionModel = timecardsBatchedCallbackSubscriptionService.createSubscriptionModel(
      this.timecardsSubscriptionCallback,
      { returnSchema: scheduleTimecardsSchema },
    );
    this.pagingModel = new PagingModel(this._loadGrid);
    this.watchColumnVisibility = createWatcherFn();
  }

  reset() {
    this.messagesSubscriptionModel.reset();
    this.messageCountsPollingModel.reset();
    this.timecardsBatchedCallbackSubscriptionModel.reset();
    this.watchColumnVisibility();
    this.updateGridShiftsQueue.kill();
    freeze(() => {
      this.state.schedules.set([]);
      this.state.grid = {} as IGrid;
    });
  }

  async load(viewMode?: ScheduleMode) {
    this.state.isLoading = true;
    try {
      if (viewMode) {
        this.state.viewMode = viewMode;
      }
      this.state.gridOptions = this.routeManager.scheduleGridOptions
        ? { ...this.routeManager.scheduleGridOptions }
        : this.getDefaultGridOptions();
      const selectedCell = this.state.selectedCell;

      this.reset();
      await this.loadGrid();
      await this.messagesSubscriptionModel.startUpdate();
      await this.messageCountsPollingModel.startUpdate();
      await this.timecardsBatchedCallbackSubscriptionModel.startUpdate();

      this.selectedCellWatcher.watch(this.grid, this.grid.rows, (foundCell) => {
        this.state.selectedCell = foundCell;
      });
      await this.setHasAutoScheduleRules();
      this.restoreSelection(selectedCell);
    } finally {
      this.state.isLoading = false;
    }
  }

  getDefaultGridOptions() {
    return {
      assignedEmployeeColumn: true,
      jobColumn: true,
      jobCellGenerator: false,
      shiftCellTemplate: true,
      shiftCellTimes: true,
      shiftCellOccupations: true,
      shiftCellReasons: true,
      shiftCellBreaks: false,
      shiftCellTimeOffRequests: true,
      shiftCellExceptionsOnly: true,
    } as IScheduleGridOptions;
  }

  private async setHasAutoScheduleRules() {
    this.state.hasAutoScheduleRules = await DataQueries.hasAutoScheduleRules();
  }

  private restoreSelection(selectedCell?: ICell) {
    if (selectedCell) {
      const row = this.state.grid.rows.find((r) => r.id === selectedCell?.row?.id);
      if (row) {
        this.state.grid.selectRows([row]);
        const cell = Object.values(row.cells).find((c) => c.column.name === selectedCell.column.name);
        if (cell) this.state.grid.selectCells([cell]);
      }
    }
  }

  loadGrid = async () => {
    await this.pagingModel.load();
  };

  private _loadGrid = async (): Promise<{ cursor?: string; totalCount: number }> => {
    const result = await this.getSchedules();
    this.state.schedules = [];
    this.state.schedules = result.schedules;
    this.state.grid = this.createGrid();
    this.setMessagesSubscriptionFilter();
    this.setMessageCountsSubscriptionFilter();
    this.setTimecardsSubscriptionFilter();
    return { cursor: result.cursor, totalCount: result.totalCount };
  };

  reloadGrid = async (delayMs = 0) => {
    if (delayMs) {
      await delay(delayMs);
    }
    await this.loadGrid();
  };

  setMessagesSubscriptionFilter() {
    if (this.viewMode === 'Job') {
      this.messagesSubscriptionModel.filter = {
        types: ['validation:assignment', 'validation:shift', 'timeoff', 'user'],
        effective: this.routeManager.effective,
        jobIds: this.schedules.map((js) => js.id),
      };
    } else {
      // Employee Mode
      this.messagesSubscriptionModel.filter = {
        types: ['validation:assignment', 'validation:shift', 'timeoff', 'user'],
        effective: this.routeManager.effective,
        employeeIds: this.schedules.map((js) => js.employee?.id),
      };
    }
  }

  setMessageCountsSubscriptionFilter() {
    this.messageCountsPollingModel.filter = {
      types: ['validation:assignment', 'validation:shift', 'timeoff', 'user'],
      areaIds: this.routeManager.filters?.areaIds,
      groupIds: this.routeManager.filters?.groupIds,
      jobIds: this.routeManager.filters?.jobIds,
      employeeIds: this.routeManager.filters?.assignedEmployeeIds,
      effective: this.routeManager.effective,
    };
  }

  setTimecardsSubscriptionFilter() {
    if (this.viewMode === 'Job') {
      this.timecardsBatchedCallbackSubscriptionModel.filter = {
        jobIds: this.schedules.map((s) => s.id),
        effective: this.routeManager.effective,
      };
    } else {
      // Employee Mode
      const groupIds = [...new Set(this.schedules.map((s) => s.group?.id).filter((id) => !!id))];
      this.timecardsBatchedCallbackSubscriptionModel.filter = {
        groupIds: groupIds,
        effective: this.routeManager.effective,
      };
    }
  }

  timecardsSubscriptionCallback = (timecardsBatchedRecords: ISubscriptionBatchedRecord<ITimecard>[]) => {
    this.updateGridShiftsQueue.push(timecardsBatchedRecords).catch((err) => console.error(err));
  };

  filterEquals(value1: any, value2: any): boolean {
    const isNull1 = value1 === undefined || value1 === null;
    const isNull2 = value2 === undefined || value2 === null;
    if (isNull1 && isNull2) return true;
    if (isNull1 || isNull2) return false;
    if (value1 === value2) return true;
    if (value1.equals) return value1.equals(value2);
    if (typeof value1 === 'number' && typeof value2 === 'number') {
      return Math.abs(value1 - value2) < 0.0001;
    }
    if (Array.isArray(value1)) {
      return value1.some((v1) => {
        if (Array.isArray(value2)) {
          return value2.some((v2) => this.filterEquals(v1, v2));
        }
        return this.filterEquals(v1, value2);
      });
    }
    return false;
  }

  getSchedulesInScheduleFilters(schedules: ISchedule[]): ISchedule[] {
    const filters = this.routeManager.filters || {};
    this.convertEmployeeIdsToScheduleEmployeeIds(filters);

    return schedules.filter((s) => {
      // Message levels filter doesn't really work here as messages are generated by the server and sent via subscription...
      // Search filter probably not possible client side.
      if (
        filters.areaIds &&
        !this.filterEquals(filters.areaIds, s.group?.area?.id) &&
        !s.schedule.some((t) => t.details?.some((d) => this.filterEquals(filters.areaIds, d.group?.area?.id)))
      )
        return false;
      if (
        filters.groupIds &&
        !this.filterEquals(filters.groupIds, s.group?.id) &&
        !s.schedule.some((t) => t.details?.some((d) => this.filterEquals(filters.groupIds, d.group?.id)))
      )
        return false;
      if (
        filters.occupationIds &&
        !this.filterEquals(filters.occupationIds, s.occupation?.id) &&
        !s.schedule.some((t) => t.details?.some((d) => this.filterEquals(filters.occupationIds, d.job?.occupation.id)))
      )
        return false;
      if (
        filters.generatorIds &&
        !this.filterEquals(filters.generatorIds, s.generator?.id) &&
        !s.schedule.some((t) => t.details?.some((d) => this.filterEquals(filters.generatorIds, d.job?.generator?.id)))
      )
        return false;
      if (
        filters.assignedEmployeeIds &&
        !this.filterEquals(filters.assignedEmployeeIds, s.employee?.id) &&
        !s.schedule.some((t) => t.details?.some((d) => this.filterEquals(filters.assignedEmployeeIds, d.job?.employee?.id)))
      )
        return false;
      if (
        filters.scheduledEmployeeIds &&
        !s.schedule.some((t) => t.details?.some((d) => this.filterEquals(filters.scheduledEmployeeIds, d.employee?.id)))
      )
        return false;
      if (filters.reasonIds && !s.schedule.some((t) => t.details?.some((d) => this.filterEquals(filters.reasonIds, d.reason?.id))))
        return false;
      if (filters.templateIds && !s.schedule.some((t) => this.filterEquals(filters.templateIds, t.template))) return false;
      if (filters.custom?.length) {
        const employee = s.employee;
        for (const udfFilter of filters.custom) {
          const udfFilterNameArr = udfFilter.field.split('.');
          let udfFilterName = udfFilterNameArr[udfFilterNameArr.length - 1];
          if (udfFilterName === 'id') {
            udfFilterName = udfFilterNameArr[udfFilterNameArr.length - 2];
          }
          const udfFilterType = udfsSubscriptionService.subscriptionModel.getUdfByName(udfFilterName)?.type;
          if (!udfFilterType) continue;
          const udfFilterValue = udfFilter.in ?? udfFilter.eq;
          let employeeUdfValue = employee?.fields?.[udfFilterName];
          if (udfFilterType === 'reference') {
            employeeUdfValue = Array.isArray(employeeUdfValue) ? employeeUdfValue.map((v) => v.id) : employeeUdfValue?.id;
          }
          if (!this.filterEquals(udfFilterValue, employeeUdfValue)) {
            return false;
          }
        }
      }
      return true;
    });
  }

  updateGridJobs = async (jobs: any[]) => {
    const effective = this.routeManager.effective;
    const jobIds = jobs.map((job: IJob) => job.id);
    if (!effective) return;

    let updatedSavedSchedules: ISavedSchedule[] | undefined;
    if (this.viewMode === 'Job') {
      updatedSavedSchedules = (await DataQueries.getGetJobSchedule(savedScheduleReturnSchema, effective, jobIds)) as
        | ISavedSchedule[]
        | undefined;
    } else {
      // employee mode
      // get the new job schedule by the employee id's of the jobs passed in, and then gather all distinct jobs that are in the grid with the matching assigned employee.
      // push these jobs onto the jobs and jobIds arrays if they don't already exist there, so that they are handled properly later.
      const employeeIds = [...new Set(jobs.filter((j) => !!j.employee).map((j) => j.employee!.id))] as string[];
      updatedSavedSchedules = (await DataQueries.getGetEmployeeSchedule(savedScheduleReturnSchema, effective, employeeIds)) as
        | ISavedSchedule[]
        | undefined;

      updatedSavedSchedules?.slice().forEach((savedSchedule) => {
        const returnedJobsWithEmployee = jobs.filter((j) => j.employee?.id === savedSchedule.id);
        const gridJobsWithEmployee = this.grid.rows
          .map((r) => this.getJobFromRow(r))
          .filter((j) => j?.employee?.id === savedSchedule.id);
        const jobsWithEmployee = returnedJobsWithEmployee.slice();
        for (const job of gridJobsWithEmployee) {
          if (!jobsWithEmployee.some((j) => j.id === job.id)) {
            jobsWithEmployee.push(job);
            jobs.push(job);
            jobIds.push(job.id);
          }
        }
        // filter out jobs that are no longer within the displayed effective range, and convert the ids of the saved schedules from employee id's to job ids,
        // creating saved schedules for every appropriate job id.
        jobsWithEmployee
          .filter((j) => effective.intersects(j.effective))
          .forEach((job, idx) => {
            if (idx === 0) {
              savedSchedule.id = job.id;
            } else {
              const newShifts = savedSchedule.schedule.map((shift) => cloneShift(shift));

              const newSavedSchedule: ISavedSchedule = {
                id: job.id,
                schedule: newShifts,
              };
              updatedSavedSchedules!.push(newSavedSchedule);
            }
          });
      });
    }

    if (updatedSavedSchedules) {
      this.sortScheduleShiftTimecards(updatedSavedSchedules);
      let updatedJobSchedules = updatedSavedSchedules
        .map((savedSchedule: ISavedSchedule) => {
          const matchingJob = jobs.find((job: IJob) => job.id === savedSchedule.id);
          return matchingJob ? (Object.assign({}, savedSchedule, matchingJob) as ISchedule) : undefined;
        })
        .filter((updatedSchedule: ISchedule | undefined) => !!updatedSchedule) as ISchedule[];
      updatedJobSchedules = this.getSchedulesInScheduleFilters(updatedJobSchedules);
      updatedSavedSchedules = updatedSavedSchedules.filter((s) => updatedJobSchedules.some((js) => js.id === s.id));

      for (const updatedJobSchedule of updatedJobSchedules as (ISchedule & { prevJobId?: string })[]) {
        for (const shift of updatedJobSchedule.schedule) {
          // filter out details with premium reason codes
          shift.details = shift.details?.filter(
            (detail: ITimecard) => detail.reason?.category !== 'premium' && detail.reason?.category !== 'other',
          );
        }
        const scheduleRow = this.grid.row(updatedJobSchedule.id);
        const rowDatas = this.createGridRows([updatedJobSchedule], this.grid.columns);
        if (scheduleRow) {
          scheduleRow.data = Object.assign({}, scheduleRow.data, rowDatas[0].data);
          scheduleRow.validate();
          scheduleRow.mergeAllColumns();
        } else {
          let rowIdx = this.grid.rows.findIndex(
            (row: IRow) => updatedJobSchedule.prevJobId && row.data.job.id === updatedJobSchedule.prevJobId,
          );
          const insertPos = rowIdx >= 0 ? ++rowIdx : this.grid.rows.length;
          this.grid.addRow(rowDatas[0], insertPos);
        }
        const newStoredJob = { ...updatedJobSchedule };
        delete newStoredJob.prevJobId;
        const storedJobIdx = this.schedules.findIndex((schedule: DeepPartial<ISchedule>) => schedule.id === newStoredJob.id);
        if (storedJobIdx >= 0) {
          this.schedules[storedJobIdx] = newStoredJob;
        } else {
          this.schedules.push(newStoredJob);
        }
      }
    }

    // if job does not have an updated schedule, it needs to be removed from the grid.
    for (const jobId of jobIds) {
      if (!updatedSavedSchedules || !updatedSavedSchedules.some((savedSchedule: ISavedSchedule) => savedSchedule.id === jobId)) {
        let newCellToSelect: ICell | undefined;
        const isOnlyRowSelected = this.grid.selectedRows.length === 1 && this.grid.selectedRows[0].id === jobId;
        if (isOnlyRowSelected) {
          const currCell = this.grid.selectedCells[0] || undefined;
          this.grid.startCell = undefined;
          newCellToSelect = currCell ? (this.grid as any).findVerticallyNearestCellWithUnselectedRow(currCell) : undefined;
        }
        this.grid.removeRow(jobId);
        const storedJobIdx = this.schedules.findIndex((schedule: DeepPartial<ISchedule>) => schedule.id === jobId);
        if (storedJobIdx >= 0) {
          this.schedules.splice(storedJobIdx, 1);
        }
        if (newCellToSelect) {
          this.grid.selectCells([newCellToSelect]);
          this.grid.startCell = newCellToSelect;
        }
      }
    }

    this.setMessagesSubscriptionFilter();
    this.setTimecardsSubscriptionFilter();
    await scheduleInfoPanelModel.load();
  };

  async updateGridShifts(timecardsBatchedRecords: ISubscriptionBatchedRecord<ITimecard>[]) {
    await this.reselectSelectedCellsAfterAction(async () => {
      const timecardsByJobId =
        this.viewMode === 'Job'
          ? this.updateJobModeGridShifts(timecardsBatchedRecords)
          : this.updateEmployeeModeGridShifts(timecardsBatchedRecords);
      const selectedCellsJobInfo = this.getJobInfo(this.selectedCells);
      let reloadInfoPanel = false;

      for (const jobId of objectKeys(timecardsByJobId)) {
        const timecardsObj = timecardsByJobId[jobId];
        if (!timecardsObj.timecardsAdded.length && !timecardsObj.timecardsDeleted.length) {
          return;
        }

        this.updateGridRowShifts(jobId, timecardsObj.timecardsAdded, timecardsObj.timecardsDeleted);

        if (!reloadInfoPanel) {
          const timecardsObjDates = [...timecardsObj.timecardsAdded, ...timecardsObj.timecardsDeleted].map((t) => t.earnedDate);
          const selectedCellsDates = selectedCellsJobInfo.find((ji) => ji.jobId === jobId)?.dates;
          if (selectedCellsDates?.some((sDate) => timecardsObjDates.some((od) => od.date === sDate?.date))) {
            reloadInfoPanel = true;
          }
        }
      }

      if (reloadInfoPanel) {
        await scheduleInfoPanelModel.load();
      }
    });
  }

  updateJobModeGridShifts(timecardsBatchedRecords: ISubscriptionBatchedRecord<ITimecard>[]) {
    const timecardsByJobId: Record<string, { timecardsAdded: ITimecard[]; timecardsDeleted: ITimecard[] }> = {};

    for (const batchedRecord of timecardsBatchedRecords) {
      for (const record of batchedRecord.records) {
        const timecard = record['timecard'] as ITimecard;
        const jobId = timecard.job?.id;
        const operation = record.operation;
        if (isNullOrUndefined(jobId) || timecard.reason?.category === 'premium' || timecard.reason?.category === 'other') {
          continue;
        }

        this.updateGridShiftsBuildTimecardsRecord(timecardsByJobId, jobId, timecard, operation);
      }
    }

    return timecardsByJobId;
  }

  updateEmployeeModeGridShifts(timecardsBatchedRecords: ISubscriptionBatchedRecord<ITimecard>[]) {
    const timecardsByEmployeeId: Record<string, { timecardsAdded: ITimecard[]; timecardsDeleted: ITimecard[] }> = {};

    for (const batchedRecord of timecardsBatchedRecords) {
      for (const record of batchedRecord.records) {
        const timecard = record['timecard'] as ITimecard;
        const jobId = timecard.job?.id;
        let operation = record.operation;
        let employeeId = timecard.employee?.id;
        if (isNullOrUndefined(employeeId)) {
          employeeId = this.schedules.find((s) => s.id === jobId)?.employee?.id;
          operation = 'deleted';
        }
        if (
          isNullOrUndefined(jobId) ||
          isNullOrUndefined(employeeId) ||
          timecard.reason?.category === 'premium' ||
          timecard.reason?.category === 'other'
        ) {
          continue;
        }

        this.updateGridShiftsBuildTimecardsRecord(timecardsByEmployeeId, employeeId, timecard, operation);
      }
    }

    const timecardsByJobId: Record<string, { timecardsAdded: ITimecard[]; timecardsDeleted: ITimecard[] }> = {};
    for (const employeeId of Object.keys(timecardsByEmployeeId)) {
      const timecardRecord = timecardsByEmployeeId[employeeId];
      const jobIdsForEmployee = this.grid.rows.filter((r) => this.getEmployeeFromRow(r)?.id === employeeId).map((r) => r.id);
      for (const jId of jobIdsForEmployee) {
        timecardsByJobId[jId] = timecardRecord;
      }
    }

    return timecardsByJobId;
  }

  protected updateGridShiftsBuildTimecardsRecord(
    record: Record<string, { timecardsAdded: ITimecard[]; timecardsDeleted: ITimecard[] }>,
    index: string,
    timecard: ITimecard,
    operation: OperationType,
  ) {
    let timecardRecord = record[index];
    if (!timecardRecord) {
      timecardRecord = { timecardsAdded: [], timecardsDeleted: [] };
      record[index] = timecardRecord;
    }

    // if timecard already "added", remove it to either make room for the new version, or as a result of the timecard later being "deleted".
    const tIdx = timecardRecord.timecardsAdded.findIndex((t) => t.id === timecard.id);
    if (tIdx >= 0) {
      timecardRecord.timecardsAdded.splice(tIdx, 1);
    }

    if (operation === 'added') {
      timecardRecord.timecardsAdded.push(timecard);
    } else if (operation === 'deleted') {
      timecardRecord.timecardsDeleted.push(timecard);
    }
  }

  updateGridRowShifts(jobId: string, timecardsToAdd: ITimecard[], timecardsToDelete: ITimecard[]) {
    const scheduleRow = this.grid.row(jobId);
    const storedSchedule = this.schedules.find((schedule: DeepPartial<ISchedule>) => schedule.id === jobId);
    if (!scheduleRow) {
      return;
    }

    const occupationId = scheduleRow.data.job.occupation.id;
    const jobStartDate = scheduleRow.data.job.effective.start;
    const jobEndDate = scheduleRow.data.job.effective.end;

    for (let i = 0; i < mainDateRangeNavModel.numOfDays; i++) {
      const columnDate = mainDateRangeNavModel.startDateStartOfDay.add(i);
      if (jobStartDate.date! > columnDate.date || (jobEndDate && jobEndDate.date! < columnDate.date)) {
        continue;
      }
      const addedTimecards = timecardsToAdd.filter((t) => t.earnedDate.date === columnDate.date);
      const deletedTimecards = timecardsToDelete.filter((t) => t.earnedDate.date === columnDate.date);
      if (!addedTimecards.length && !deletedTimecards.length) {
        continue;
      }
      const storedScheduleShiftIdx = storedSchedule?.schedule?.findIndex((s) => s.earnedDate?.date === columnDate.date) ?? -1;
      let shift = storedSchedule?.schedule?.[storedScheduleShiftIdx] as IShift | undefined;
      if (!shift) {
        shift = {
          earnedDate: columnDate.clone(),
          details: addedTimecards,
        } as IShift;
      } else {
        shift = { ...shift };
        if (!shift.details) {
          shift.details = [];
        } else {
          // handle case where an existing timecard was updated, and needs to be removed from the original list of shift details,
          // as the new version is present in the addedTimecards list.
          shift.details = shift.details.filter((detail) => !addedTimecards.some((at) => at.id === detail.id));
        }
        shift.details.push(...addedTimecards);
        shift.details = shift.details.filter((detail) => !deletedTimecards.some((dt) => dt.id === detail.id));
      }

      shift.template = shift.details!.filter((d) => d.job?.id === jobId).find((d) => !!d.template)?.template;
      this.sortShiftTimecardData(shift.details as ITimecard[], true);

      const value = shift.details!.length ? new ScheduleShiftCellData(jobId, occupationId, shift as IShift) : undefined;
      scheduleRow.data[`shift${i}`] = value;
      if (storedSchedule?.schedule?.[storedScheduleShiftIdx]) {
        storedSchedule.schedule[storedScheduleShiftIdx] = shift;
      }
    }
  }

  reselectSelectedCellsAfterAction = async (action: () => Promise<void>) => {
    const selectedCells = this.grid.selectedCells.slice();
    await action();
    if (selectedCells.length) {
      this.grid.selectCells(selectedCells);
    }
  };

  getSchedules = async (): Promise<{ schedules: DeepPartial<ISchedule>[]; cursor?: string; totalCount: number }> => {
    const effective = this.routeManager.effective!;
    const { groups, sorts } = this.routeManager;
    let { filters } = this.routeManager;

    if (!filters) filters = {};
    this.convertEmployeeIdsToScheduleEmployeeIds(filters);

    const result =
      this.viewMode === 'Job'
        ? await DataQueries.getJobSchedule(
            scheduleReturnSchema,
            effective,
            filters,
            groups,
            sorts,
            this.pagingModel.itemsPerPage,
            this.pagingModel.currentPage,
          )
        : await DataQueries.getEmployeeSchedule(
            scheduleReturnSchema,
            effective,
            filters,
            groups,
            sorts,
            this.pagingModel.itemsPerPage,
            this.pagingModel.currentPage,
          );
    const schedules = result?.data;
    if (schedules?.length) {
      for (const schedule of schedules as ISchedule[]) {
        for (const shift of schedule.schedule) {
          // filter out details with premium reason codes
          shift.details = shift.details?.filter(
            (detail: ITimecard) => detail.reason?.category !== 'premium' && detail.reason?.category !== 'other',
          );
        }
        if (!schedule.fields) {
          schedule.fields = {};
        }
        if (isNullOrUndefined(schedule.fields['_autoCreateVacancyForAbsenceReasons:b'])) {
          schedule.fields['_autoCreateVacancyForAbsenceReasons:b'] = false;
        }
      }
      this.sortScheduleShiftTimecards(schedules as ISchedule[]);
    }
    return { schedules: schedules, cursor: result.cursor, totalCount: result.totalCount };
  };

  private convertEmployeeIdsToScheduleEmployeeIds(filters: ScheduleFilterInput) {
    if (filters.employeeIds) {
      filters.scheduledEmployeeIds = filters.employeeIds;
      delete filters.employeeIds;
    }
  }

  sortScheduleShiftTimecards(schedules: ISchedule[] | ISavedSchedule[]) {
    for (const schedule of schedules as ISchedule[]) {
      for (const shift of schedule.schedule) {
        const details = shift.details;
        if (!details) return;
        this.sortShiftTimecardData(details, true);
      }
    }
  }

  sortShiftTimecardData = (timecardData: ITimecard[], scheduleLoad: boolean = false) => {
    timecardData.sort((timecard1: ITimecard, timecard2: ITimecard) => {
      const shiftNum1 = timecard1.shift;
      const shiftNum2 = timecard2.shift;
      const hasShiftNum1 = !isNullOrUndefined(shiftNum1);
      const hasShiftNum2 = !isNullOrUndefined(shiftNum2);
      let sortResult =
        hasShiftNum1 && !hasShiftNum2
          ? -1
          : !hasShiftNum1 && hasShiftNum2
            ? 1
            : shiftNum1! < shiftNum2!
              ? -1
              : shiftNum1! > shiftNum2!
                ? 1
                : 0;
      if (sortResult === 0) {
        const isAbs1 = timecard1.reason?.category === 'absence';
        const isAbs2 = timecard2.reason?.category === 'absence';
        sortResult = !isAbs1 && isAbs2 ? -1 : isAbs1 && !isAbs2 ? 1 : 0;
      }
      if (sortResult === 0) {
        const startTime1 = scheduleLoad ? timecard1.effective?.start?.date : timecard1.start;
        const startTime2 = scheduleLoad ? timecard2.effective?.start?.date : timecard2.start;
        const hasStartTime1 = !isNullOrUndefined(startTime1);
        const hasStartTime2 = !isNullOrUndefined(startTime2);
        sortResult =
          !hasStartTime1 && hasStartTime2
            ? -1
            : hasStartTime1 && !hasStartTime2
              ? 1
              : !hasStartTime1 && !hasStartTime2
                ? 0
                : startTime1! < startTime2!
                  ? -1
                  : startTime1! > startTime2!
                    ? 1
                    : 0;
      }
      if (sortResult === 0) {
        const endTime1 = scheduleLoad ? timecard1.effective?.end?.date : timecard1.end;
        const endTime2 = scheduleLoad ? timecard2.effective?.end?.date : timecard2.end;
        const hasEndTime1 = !isNullOrUndefined(endTime1);
        const hasEndTime2 = !isNullOrUndefined(endTime2);
        sortResult =
          !hasEndTime1 && hasEndTime2
            ? -1
            : hasEndTime1 && !hasEndTime2
              ? 1
              : !hasEndTime1 && !hasEndTime2
                ? 0
                : endTime1! < endTime2!
                  ? -1
                  : endTime1! > endTime2!
                    ? 1
                    : 0;
      }
      if (sortResult === 0) {
        const reason1 = timecard1.reason;
        const reason2 = timecard2.reason;
        const hasReason1 = !isNullOrUndefined(reason1);
        const hasReason2 = !isNullOrUndefined(reason2);
        sortResult =
          !hasReason1 && hasReason2
            ? -1
            : hasReason1 && !hasReason2
              ? 1
              : !hasReason1 && !hasReason2
                ? 0
                : reason1!.id.localeCompare(reason2!.id);
      }
      if (sortResult === 0) {
        const units1 = timecard1.units;
        const units2 = timecard2.units;
        sortResult = units1! < units2! ? -1 : units1! > units2! ? 1 : 0;
      }
      if (sortResult === 0) {
        const timecardId1 = timecard1.id;
        const timecardId2 = timecard2.id;
        sortResult = timecardId1!.localeCompare(timecardId2!);
      }
      return sortResult;
    });
  };

  get schedules(): DeepPartial<ISchedule>[] {
    return this.state.schedules;
  }

  get grid(): IGrid {
    return this.state.grid;
  }

  get gridOptions(): IScheduleGridOptions {
    return this.state.gridOptions;
  }

  get selectedCells(): ICell[] {
    return this.grid.selectedCells || [];
  }

  get selectedCell(): ICell | undefined {
    return this.selectedCells[0];
  }

  get isMultiSelect(): boolean {
    return this.selectedCells.length > 1;
  }

  get isLoading(): boolean {
    return this.state.isLoading || this.pagingModel.loading || this.routeManager.isLoading;
  }

  get viewMode(): ScheduleMode {
    return this.state.viewMode;
  }

  get hasResults(): boolean {
    return !!this.schedules?.length;
  }

  get hasRows(): boolean {
    return !!this.grid?.rows?.length;
  }

  get routeManager(): PlanRouteManagerBase {
    return this.viewMode === 'Job' ? jobScheduleRouteManager : employeeScheduleRouteManager;
  }

  get viewOnly(): boolean {
    return mainPageModel.scheduleViewOnly;
  }

  get hasGroupFilters(): boolean {
    return !!this.routeManager.filters?.groupIds?.length;
  }

  get hasAutoScheduleRules(): boolean {
    return this.state.hasAutoScheduleRules;
  }

  get gridIsReorderable(): boolean {
    return !this.viewOnly && this.hasGroupFilters;
  }

  getCellType(cell: ICell): ScheduleCellType {
    if (cell.column.name === 'employee') {
      return 'Employee';
    }
    if (cell.column.name === 'job') {
      return 'Job';
    }
    return 'Shift';
  }

  getJobCellFromRow(row: IRow): ICell {
    return row.cells['job'];
  }

  getJobCellFromCell(cell: ICell): ICell {
    return this.getJobCellFromRow(cell.row);
  }

  getJobCellsFromCells(cells: ICell[]): ICell[] {
    const jobCells = cells.map((c) => this.getJobCellFromRow(c.row));
    return [...new Map(jobCells.map((jc) => [jc.id, jc])).values()];
  }

  getJobFromRow(row: IRow): IJob {
    return this.getJobCellFromRow(row).value;
  }

  getJobFromCell(cell: ICell): IJob {
    return this.getJobFromRow(cell.row);
  }

  getJobsFromCells(cells: ICell[]): IJob[] {
    const jobs = cells.map((c) => this.getJobFromRow(c.row));
    return [...new Map(jobs.map((j) => [j.id, j])).values()];
  }

  getEmployeeCellFromRow(row: IRow): ICell {
    return row.cells['employee'];
  }

  getEmployeeCellFromCell(cell: ICell): ICell {
    return this.getEmployeeCellFromRow(cell.row);
  }

  getEmployeeFromRow(row: IRow): IEmployee | undefined {
    return this.getEmployeeCellFromRow(row).value;
  }

  getEmployeeFromCell(cell: ICell): IEmployee | undefined {
    return this.getEmployeeFromRow(cell.row);
  }

  getShiftIdx(idx: number) {
    return idx - 2;
  }

  getShiftCellEarnedDate(cell: ICell): UTC {
    const idx = this.getShiftIdx(cell.columnPosition);
    return mainDateRangeNavModel.startDate.add(idx);
  }

  getShiftColumnEarnedDateByPosition(columnPosition: number): UTC {
    return mainDateRangeNavModel.startDateStartOfDay.add(this.getShiftIdx(columnPosition));
  }

  getColumnIsPostedByPosition(columnPosition: number): boolean {
    if (!mainPageModel.postedDate) {
      return false;
    }
    const earnedDate = this.getShiftColumnEarnedDateByPosition(columnPosition);
    return earnedDate <= mainPageModel.postedDate;
  }

  getMatchingTimecardTemplateForAbsenceDetail(
    timecard: ITimecard,
    timecardTemplates?: ITimecardTemplate[],
  ): ITimecardTemplate | undefined {
    // don't match absence, premium, or break reasons of the template. (breaks should be the only valid template reason category I'd think)
    timecardTemplates = timecardTemplates?.filter(
      (tt: ITimecardTemplate) =>
        tt.reason?.category !== 'absence' && tt.reason?.category !== 'break' && tt.reason?.category !== 'premium',
    );
    if (!timecardTemplates?.length) {
      return undefined;
    }

    const timecardTimes = timestampRangeToNumericTime(timecard.effective, timecard.earnedDate);
    const timecardStart = (timecard.start ?? timecardTimes.start) as number | undefined;
    const timecardEnd = (timecard.end ?? timecardTimes.end) as number | undefined;
    const hasTimes = !isNullOrUndefined(timecardStart) && !isNullOrUndefined(timecardEnd);

    let matchingTimecardTemplate = timecardTemplates.find((timecardTemplate: ITimecardTemplate) => {
      const ttHasTimes = !isNullOrUndefined(timecardTemplate.start) && !isNullOrUndefined(timecardTemplate.end);
      return (
        timecard.units === timecardTemplate.units &&
        ((!hasTimes && !ttHasTimes) ||
          (hasTimes && ttHasTimes && timecardStart === timecardTemplate.start && timecardEnd === timecardTemplate.end))
      );
    });

    if (!matchingTimecardTemplate) {
      matchingTimecardTemplate = timecardTemplates.find((timecardTemplate: ITimecardTemplate) => {
        const ttHasTimes = !isNullOrUndefined(timecardTemplate.start) && !isNullOrUndefined(timecardTemplate.end);
        return (
          timecard.units! <= timecardTemplate.units! &&
          ((!hasTimes && !ttHasTimes) ||
            (hasTimes && ttHasTimes && timecardStart! >= timecardTemplate.start! && timecardEnd! <= timecardTemplate.end!))
        );
      });
    }

    return matchingTimecardTemplate;
  }

  getMatchingTimecardTemplateReasonForAbsenceDetail(
    timecard: ITimecard,
    timecardTemplates?: ITimecardTemplate[],
  ): IReason | undefined {
    if (!timecardTemplates?.length) {
      return undefined;
    }

    const matchingTimecardTemplateReason = this.getMatchingTimecardTemplateForAbsenceDetail(timecard, timecardTemplates)?.reason;
    if (!matchingTimecardTemplateReason) {
      const templateWorkedReasons = timecardTemplates
        .map((timecardTemplate: ITimecardTemplate) => timecardTemplate.reason)
        .filter((reason: IReason | undefined) => reason?.category === 'worked');
      return templateWorkedReasons?.[0];
    }

    return matchingTimecardTemplateReason;
  }

  isNewRow(row: IRow): boolean {
    return !row.data.job;
  }

  moveJobPosition = async (jobId: string, byRows: number, isCallback: boolean = false) => {
    const jobRow = this.grid.row(jobId);
    if (!jobRow) {
      return;
    }
    if (!isCallback) {
      if (!this.grid.moveRow(jobRow.id, byRows)) {
        return;
      }
    }

    if (this.isNewRow(jobRow)) {
      // moved row is a new row, so no server call needed, as it will get assigned its position on save.
      return;
    }

    const jobRows = this.grid.rows.filter((row: IRow) => !this.isNewRow(row));
    const jobRowIndex = jobRows.findIndex((row: IRow) => row.id === jobRow!.id);
    const jobRowGroupRowId = jobRow.groupRow?.id;
    let rowBeforeJobRow: IRow | undefined;
    let beforeJobId: string | undefined;
    if (this.routeManager.groups?.length || (this.routeManager.sorts && Object.keys(this.routeManager.sorts).length)) {
      // jobs are grouped, need to handle positions differently.
      if (byRows > 0) {
        rowBeforeJobRow = jobRows[jobRowIndex - 1];
        beforeJobId = rowBeforeJobRow && rowBeforeJobRow.groupRow?.id === jobRowGroupRowId ? rowBeforeJobRow.id : undefined;
      } else {
        const jobsByJobPosition = jobRows
          .slice()
          .sort((r1: IRow, r2: IRow) =>
            r1.data.job.position < r2.data.job.position ? -1 : r1.data.job.position > r2.data.job.position ? 1 : 0,
          );
        const rowAfterJobRow = jobRows[jobRowIndex + 1];
        const rowAfterJobRowPosition =
          rowAfterJobRow && rowAfterJobRow.groupRow?.id === jobRowGroupRowId
            ? jobsByJobPosition.findIndex((jRow: IRow) => jRow.id === rowAfterJobRow!.id)
            : -1;
        if (rowAfterJobRowPosition < 0) {
          // no server posiiton set necessary
          return;
        }
        rowBeforeJobRow = jobsByJobPosition[rowAfterJobRowPosition - 1];
        beforeJobId = rowBeforeJobRow?.id;
      }
    } else {
      rowBeforeJobRow = jobRows[jobRowIndex - 1];
      beforeJobId = rowBeforeJobRow?.id;
    }

    if (!beforeJobId && byRows > 0) {
      // no server position set necessary
      return;
    }

    const result = await DataMutations.moveJob(jobId, beforeJobId);
    if (isNullOrUndefined(result)) {
      notifications.error('Job Move Failed. Move will not persist');
      return;
    }
    jobRow.data.job.position = result!.position;
  };

  swapJobPositions = async (jobId1: string, jobId2: string, isCallback: boolean = false) => {
    const jobRow1 = this.grid.row(jobId1);
    const jobRow2 = this.grid.row(jobId2);
    if (!jobRow1 || !jobRow2) {
      return;
    }
    if (!isCallback) {
      if (!this.grid.swapRows(jobRow1.id, jobRow2.id)) {
        return;
      }
    }

    if (this.isNewRow(jobRow1) && this.isNewRow(jobRow2)) {
      // swapped rows are new rows, so no server call needed.
      return;
    }

    const jobRows = this.grid.rows.filter((row: IRow) => !this.isNewRow(row));
    let beforeJobIndex1: number;
    let beforeJobIndex2: number;
    let beforeJobId1: string;
    let beforeJobId2: string;
    if (this.routeManager.groups?.length || (this.routeManager.sorts && Object.keys(this.routeManager.sorts).length)) {
      // jobs are grouped or sorted, need to handle positions differently.
      const jobsByJobPosition = jobRows
        .slice()
        .sort((r1: IRow, r2: IRow) =>
          r1.data.job.position < r2.data.job.position ? -1 : r1.data.job.position > r2.data.job.position ? 1 : 0,
        );
      beforeJobIndex1 = jobsByJobPosition.findIndex((jRow: IRow) => jRow.id === jobRow2!.id) - 1;
      beforeJobIndex2 = jobsByJobPosition.findIndex((jRow: IRow) => jRow.id === jobRow1!.id) - 1;
      beforeJobId1 = jobsByJobPosition[beforeJobIndex1]?.id;
      beforeJobId2 = jobsByJobPosition[beforeJobIndex2]?.id;
    } else {
      const jobRowIdx1 = jobRows.findIndex((row: IRow) => row.id === jobRow1!.id);
      const jobRowIdx2 = jobRows.findIndex((row: IRow) => row.id === jobRow2!.id);
      beforeJobIndex1 = jobRowIdx1 - 1;
      beforeJobIndex2 = jobRowIdx2 - 1;
      beforeJobId1 = jobRows[beforeJobIndex1]?.id;
      beforeJobId2 = jobRows[beforeJobIndex2]?.id;
    }

    if (!this.isNewRow(jobRow1)) {
      const result1 = await DataMutations.moveJob(jobId1, beforeJobId1);
      if (isNullOrUndefined(result1)) {
        notifications.error('Job Swap Failed. Swap will not persist');
        return;
      }
      jobRow1.data.job.position = result1!.position;
    }
    if (!this.isNewRow(jobRow2)) {
      const result2 = await DataMutations.moveJob(jobId2, beforeJobId2);
      if (isNullOrUndefined(result2)) {
        notifications.error('Job Swap Failed. Swap will not persist');
        return;
      }
      jobRow2.data.job.position = result2!.position;
    }
  };

  addJob() {
    if (!mainPageModel.canAddJobs || scheduleControlsModel.mode !== 'group') {
      return;
    }
    const addedRow = this.grid.insertRowAtSelection({ id: guid() });
    const addedJobCell = this.getJobCellFromRow(addedRow);
    this.grid.selectCells([addedJobCell]);
    setTimeout(() => addedJobCell.setFocus(), 0);
  }

  canAddShift(): boolean {
    if (
      !this.selectedCell ||
      this.isMultiSelect ||
      this.selectedCell.value ||
      this.getCellType(this.selectedCell) !== 'Shift' ||
      infoPanelShiftViewModel.shift
    ) {
      return false;
    }

    const job = this.getJobFromCell(this.selectedCell);
    const selectedCellDate = this.getShiftCellEarnedDate(this.selectedCell);
    return !!job?.effective.inRange(selectedCellDate);
  }

  getVacanciesFromDetails(details?: ITimecard[]): ITimecard[] {
    if (!details?.length) {
      return [];
    }

    return details.filter((d) => !d.employee && d.job?.id && d.earnedDate);
  }

  getVacanciesFromSelection() {
    return this.selectedCells.flatMap((c) => this.getVacanciesFromDetails(c.value?.details));
  }

  *getSelectedShifts(predicate: (timecard: ITimecard) => boolean): Iterable<ITimecard> {
    for (const c of this.selectedCells) {
      for (const e of c.value?.details ?? []) {
        if (e && predicate(e)) {
          yield e;
        }
      }
    }
  }

  canAutoSchedule() {
    return mainPageModel.canEditSchedule && this.getVacanciesFromSelection().length && this.hasAutoScheduleRules;
  }

  autoSchedule() {
    if (!this.canAutoSchedule()) {
      return;
    }

    const vacancies = this.getVacanciesFromSelection();
    if (!vacancies?.length) return;

    DataMutations.runAutoSchedule(vacancies.map((vacancy) => ({ jobId: vacancy.job!.id, earnedDate: vacancy.earnedDate })));
  }

  // createJob = async (jobData: IJob, prevJobId?: string) => {
  //   const jobInput: JobInput = {
  //     id:           guid(),
  //     groupId:      jobData.group.id,
  //     generatorId:  jobData.generator?.id,
  //     occupationId: jobData.occupation.id,
  //     employeeId:   jobData.employee?.id,
  //     effective:    jobData.effective,
  //     cycleOffset:  jobData.cycleOffset,
  //     position:     jobData.position,
  //   };

  //   let result = await DataMutations.insertJobs('', [jobInput], prevJobId);
  //   if (result && result.length) {
  //     scheduleModel.reloadGrid();
  //     notifications.success('Job Created');
  //   } else {
  //     notifications.error('Job Creation Failed');
  //   }
  // }

  // updateJob = async (jobId: string, jobPosition: number, jobData: IJob) => {
  //   const jobInput: JobInput = {
  //     id:           jobId,
  //     groupId:      jobData.group.id,
  //     generatorId:  jobData.generator?.id,
  //     occupationId: jobData.occupation.id,
  //     employeeId:   jobData.employee?.id,
  //     effective:    jobData.effective,
  //     cycleOffset:  jobData.cycleOffset,
  //     position:     jobPosition,
  //   };

  //   let result = await DataMutations.upsertJobs('', [jobInput]);
  //   if (result && result.length) {
  //     scheduleModel.reloadGrid();
  //     notifications.success('Job Updated');
  //   } else {
  //     notifications.error('Job Update Failed');
  //   }
  // }

  // updateJobAssignedEmployee = async (job: IJob, employeeId?: string) => {
  //   const jobInput: JobInput = {
  //     id:           job.id,
  //     groupId:      job.group.id,
  //     generatorId:  job.generator?.id,
  //     occupationId: job.occupation.id,
  //     employeeId:   employeeId,
  //     effective:    job.effective,
  //     cycleOffset:  job.cycleOffset,
  //     position:     job.position,
  //   };
  //   let result = await DataMutations.upsertJobs('', [jobInput]);
  //   if (result && result.length) {
  //     scheduleModel.reloadGrid();
  //     notifications.success('Job Assigned Employee Updated');
  //   } else {
  //     notifications.error('Job Assigned Employee Update Failed');
  //   }
  // }

  updateJobEffectiveRange = async (jobId: string, newEffective: DateRange, removeOverlappingAssignments: boolean) => {
    let result = await DataMutations.updateJobEffectiveRange(jobReturnSchema, jobId, newEffective, removeOverlappingAssignments);
    if (result) {
      result = { ...result, prevJobId: result.id !== jobId ? jobId : undefined } as any;
      await this.updateGridJobs([result]);
      notifications.success('Job Effective Range Updated.');
      return true;
    }
    notifications.error('Job Effective Range Update Failed');
    return false;
  };

  duplicateJob = async (
    jobData: any,
    count: number | undefined,
    removeOverlappingAssignments: boolean,
    prevJobId?: string,
  ): Promise<boolean> => {
    const jobInput: JobInput = {
      id: guid(),
      groupId: jobData.group?.id,
      generatorId: jobData.generator?.id,
      occupationId: jobData.occupation?.id,
      employeeId: jobData.employee?.id,
      cycleOffset: jobData.cycleOffset,
      position: jobData.position,
      effective: jobData.effective,
      fields: jobData.fields,
    };
    const jobInputs: JobInput[] = [jobInput];
    count ??= 1;

    for (let i = 1; i < count; i++) {
      const job = Object.assign({}, jobInput);
      job.id = guid();
      job.effective = job.effective.clone();
      jobInputs.push(job);
    }

    const { filters } = this.routeManager;
    const duplicateJobInView =
      (!filters?.areaIds?.length || filters.areaIds.some((aId) => aId === jobData.area.id)) &&
      (!filters?.groupIds?.length || filters.groupIds.some((gId) => gId === jobData.group.id));

    if (!duplicateJobInView) {
      prevJobId = undefined;
    }

    let result = await DataMutations.insertJobs(jobReturnSchema, jobInputs, removeOverlappingAssignments, prevJobId);
    if (result?.length) {
      if (duplicateJobInView) {
        // only update grid if the duplicated job has an area/group that correspond to the area / group filters of the current schedule.
        result = result.map((j: any) => ({ ...j, prevJobId: prevJobId }) as any);
        await this.updateGridJobs(result);
      }
      notifications.success(`Job Duplicated ${count} Times`);
      return true;
    }
    notifications.error('Job Duplication Failed');
    return false;
  };

  duplicateJobs = async (jobData: any[], removeOverlappingAssignments: boolean, prevJobId?: string): Promise<boolean> => {
    if (scheduleControlsModel.mode !== 'group') {
      return false;
    }

    const jobInputs: JobInput[] = jobData.map((jd) => ({
      id: guid(),
      groupId: jd.group?.id,
      generatorId: jd.generator?.id,
      occupationId: jd.occupation?.id,
      employeeId: jd.employee?.id,
      cycleOffset: jd.cycleOffset,
      position: jd.position,
      effective: jd.effective,
      fields: jd.fields,
    }));

    const { filters } = this.routeManager;
    const duplicateJobInView =
      (!filters?.areaIds?.length || filters.areaIds.some((aId) => aId === jobData[0].area.id)) &&
      (!filters?.groupIds?.length || filters.groupIds.some((gId) => gId === jobData[0].group.id));

    if (!duplicateJobInView) {
      prevJobId = undefined;
    }

    let result = await DataMutations.insertJobs(jobReturnSchema, jobInputs, removeOverlappingAssignments, prevJobId);
    if (result?.length) {
      if (duplicateJobInView) {
        // only update grid if the duplicated job has an area/group that correspond to the area / group filters of the current schedule.
        result = result.map((j: any) => ({ ...j, prevJobId: prevJobId }) as any);
        await this.updateGridJobs(result);
      }
      notifications.success('Jobs Duplicated');
      return true;
    }
    notifications.error('Job Duplication Failed');
    return false;
  };

  deleteJobs = async (jobIds?: string[]) => {
    if (!jobIds?.length) {
      this.grid.removeSelectedRows();
      return;
    }
    const isMultiple = jobIds.length > 1;
    const result = await DataMutations.deleteJobs(jobIds);
    if (result) {
      this.grid.removeSelectedRows();
      notifications.success(`Job${isMultiple ? 's' : ''} Deleted`);
    } else {
      notifications.error(`Job${isMultiple ? 's' : ''} Deletion Failed`);
    }
  };

  archiveJob = async (_jobId: string) => {
    // const result = await DataMutations.archiveJobs([jobId]);
    const result = false;
    if (result) {
      notifications.success('Job Archived');
    } else {
      notifications.error('Job Archiving Failed');
    }
  };

  unarchiveJob = async (_jobId: string) => {
    // const result = await DataMutations.unarchiveJobs([jobId]);
    const result = false;
    if (result) {
      notifications.success('Job Unarchived');
    } else {
      notifications.error('Job Unarchiving Failed');
    }
  };

  unassignEmployeeFromJob = async (job: IJob) => {
    const jobInput: JobInput = {
      id: job.id,
      groupId: job.group.id,
      generatorId: job.generator?.id,
      occupationId: job.occupation.id,
      employeeId: undefined,
      effective: job.effective,
      cycleOffset: job.cycleOffset,
      position: job.position,
      fields: job.fields,
    };

    const result = await DataMutations.upsertJobs(jobReturnSchema, [jobInput], false);
    if (result) {
      await this.updateGridJobs(result);
      notifications.success('Employee Unassigned');
    } else {
      notifications.error('Employee Unassignment Failed');
    }
  };

  appendToShift = async (prevShiftDetails: ITimecard[], newShiftData: any) => {
    if (!newShiftData?.details?.length) {
      return;
    }

    const timecardInputs: TimecardInput[] = [];
    const jobId = newShiftData.jobId;
    const groupId = newShiftData.groupId;
    const earnedDate: UTC = newShiftData.earnedDate;
    let maxPrevShiftNum = -1;
    for (const timecardData of prevShiftDetails) {
      const timecardInput = timecardToTimecardInput(timecardData);
      timecardInputs.push(timecardInput);
      const timecardShift = timecardData.shift ?? 0;
      if (timecardShift > maxPrevShiftNum) {
        maxPrevShiftNum = timecardShift;
      }
    }
    for (const timecardData of newShiftData) {
      const timecard = { ...timecardData };
      let effective = timecardData.effective;
      if (effective) {
        const dayDiff = earnedDate.clone().startOfDay().subtractDate(effective.start.clone().startOfDay());
        const start = effective.start.add(dayDiff);
        const end = effective.end.add(dayDiff);
        effective = new TimestampRange(start, end);
      }
      timecard.effective = effective;
      timecard.id = guid();
      timecard.earnedDate = earnedDate;
      timecard.createdDate = UTC.now();
      timecard.shift = maxPrevShiftNum + (timecard.shift ?? 0) + 1;
      const timecardInput = timecardToTimecardInput(timecard);
      timecardInput.jobId = jobId;
      timecardInput.groupId = groupId;
      timecardInputs.push(timecardInput);
    }

    let result: any;
    if (this.viewMode === 'Job') {
      result = await DataMutations.saveJobTimecards(timecardInputs);
    } else {
      // employee mode
      const employeeId = newShiftData.employeeId;
      for (const ti of timecardInputs) {
        ti.employeeId = employeeId;
      }
      const timecardsToSave = [
        {
          earnedDate: earnedDate,
          employeeId: employeeId,
          timecards: timecardInputs,
        },
      ] as SaveTimecardInput[];

      result = await DataMutations.saveEmployeeTimecards(timecardsToSave);
    }

    if (result) {
      notifications.success('Shift(s) Updated');
    } else {
      notifications.error('Shift(s) Update Failed');
    }
  };

  pasteToShifts = async (newShiftDatas: any[]) => {
    if (!newShiftDatas?.length) {
      return;
    }

    const timecardInputs: TimecardInput[] = [];
    const employeeTimecardsToSave: { [key: string]: SaveTimecardInput } = {};
    for (const shiftData of newShiftDatas) {
      const earnedDate: UTC = shiftData.earnedDate;
      for (const timecardData of shiftData.details) {
        const timecard = { ...timecardData };
        let effective = timecardData.effective;
        if (effective) {
          const dayDiff = earnedDate.clone().startOfDay().subtractDate(timecard.earnedDate.clone().startOfDay());
          const start = effective.start.add(dayDiff);
          const end = effective.end.add(dayDiff);
          effective = new TimestampRange(start, end);
        }
        timecard.effective = effective;
        timecard.id = guid();
        switch (typeof timecardData.fields) {
          case 'object':
            timecard.fields = Object.assign({}, timecardData.fields);
            break;

          case 'string':
            timecard.fields = JSON.parse(timecardData.fields);
            break;
        }
        timecard.earnedDate = earnedDate;
        timecard.createdDate = UTC.now();
        const timecardInput = timecardToTimecardInput(timecard);
        if (this.viewMode === 'Job') {
          timecardInput.jobId = shiftData.jobId;
          timecardInput.occupationId = shiftData.occupationId;
          timecardInput.groupId = shiftData.groupId;
        } else if (this.viewMode === 'Employee') {
          const employeeId = shiftData.employeeId;
          timecardInput.employeeId = employeeId;
          const key = `${earnedDate}${employeeId}`;
          if (!employeeTimecardsToSave[key]) {
            employeeTimecardsToSave[key] = {
              earnedDate: earnedDate,
              employeeId: employeeId,
              timecards: [timecardInput],
            } as SaveTimecardInput;
          } else {
            employeeTimecardsToSave[key].timecards?.push(timecardInput);
          }
        }
        timecardInputs.push(timecardInput);
      }
    }

    let result: any;
    if (this.viewMode === 'Job') {
      result = await DataMutations.saveJobTimecards(timecardInputs);
    } else {
      // employee mode
      result = await DataMutations.saveEmployeeTimecards(Object.values(employeeTimecardsToSave));
    }

    if (result) {
      notifications.success('Shift(s) Updated');
    } else {
      notifications.error('Shift(s) Update Failed');
    }
  };

  duplicateShiftScheduleSchema = `
    id
    schedule {
      earnedDate
      details {
        id
        earnedDate
        effective
        accountCode
        code
        units
        factor
        rate
        amount
        shift
        createdBy
        createdDate
        template {
          id
          name
        }
        job {
          id
        }
        group {
          id
          name
          area {
            id
            name
          }
        }
        tags
        fields
        occupation {
          id
          name
        }
        employee {
          id
          name
        }
        reason {
          id
          name
          category
        }
        overtimeReason {
          id
          name
        }
      }
    }
  `;

  duplicateShifts = async (shiftData: IDuplicateShiftData[], count: number = 1): Promise<boolean> => {
    const timecardInputs: TimecardInput[] = [];
    const earnedDates = shiftData.map((sd) => sd.earnedDate);
    const dateRange = new DateRange(LQ(earnedDates).min(), LQ(earnedDates).max());
    const jobFilter = { jobIds: shiftData.map((sd) => sd.jobId) };

    const scheduleResult =
      this.viewMode === 'Job'
        ? await DataQueries.getJobSchedule(this.duplicateShiftScheduleSchema, dateRange, jobFilter)
        : await DataQueries.getEmployeeSchedule(this.duplicateShiftScheduleSchema, dateRange, jobFilter);

    const schedulesById = scheduleResult?.data?.reduce((prev, curr) => {
      prev[curr.id!] = curr;
      return prev;
    }, {} as any);

    for (const sd of shiftData) {
      const scheduleKey = this.viewMode === 'Job' ? sd.jobId : (sd.employee?.id ?? '');
      const prevExistingTimecards = schedulesById[scheduleKey]?.schedule?.find((s: any) =>
        s.earnedDate.equals(sd.earnedDate),
      )?.details;
      let maxPrevShiftNum = -1;
      if (prevExistingTimecards) {
        for (const timecard of prevExistingTimecards) {
          const timecardInput = timecardToTimecardInput(timecard);
          timecardInputs.push(timecardInput);
          const timecardShift = timecard.shift ?? 0;
          if (timecardShift > maxPrevShiftNum) {
            maxPrevShiftNum = timecardShift;
          }
        }
      }

      const maxShiftDataShiftNum = maxPrevShiftNum + Math.max(...sd.details.map((d) => d.shift ?? 0)) + 1;
      for (const timecardData of sd.details) {
        const timecard = { ...timecardData };
        const dayDiff = sd.earnedDate.clone().startOfDay().subtractDate(timecard.earnedDate.clone().startOfDay());
        timecard.id = guid();
        timecard.earnedDate = sd.earnedDate;
        timecard.createdDate = UTC.now();
        timecard.effective =
          timecard.effective && new TimestampRange(timecard.effective.start.add(dayDiff), timecard.effective.end.add(dayDiff));
        timecard.employee = sd.employee;
        timecard.shift = maxPrevShiftNum + (timecard.shift ?? 0) + 1;

        if (sd.reason && timecard.reason?.category !== 'break') {
          timecard.reason = sd.reason;
        }

        const timecardInput = timecardToTimecardInput(timecard);
        timecardInput.groupId = timecardInput.groupId || this.routeManager.groupId;
        timecardInput.jobId = timecardInput.jobId || this.getJobFromCell(this.selectedCell!).id;
        timecardInputs.push(timecardInput);
        for (let i = 1; i < count; i++) {
          const duplicateTimecardInput = Object.assign({}, timecardInput);
          duplicateTimecardInput.id = guid();
          duplicateTimecardInput.effective = duplicateTimecardInput.effective?.clone();
          duplicateTimecardInput.shift = maxShiftDataShiftNum + i;
          timecardInputs.push(duplicateTimecardInput);
        }
      }
    }

    let result: any;
    if (this.viewMode === 'Job') {
      result = await DataMutations.saveJobTimecards(timecardInputs);
    } else {
      const timecardsToSave: SaveTimecardInput[] = [];
      const groupedEmployeeTimecards = LQ(timecardInputs).groupBy('employeeId');
      for (const employeeId in groupedEmployeeTimecards) {
        const employeeTimecards = groupedEmployeeTimecards[employeeId];
        const employeeTimecardsByDay = LQ(employeeTimecards).groupBy('earnedDate');
        for (const earnedDate in employeeTimecardsByDay) {
          const dailyTimecards = employeeTimecardsByDay[earnedDate];
          const day = dailyTimecards[0].earnedDate;
          timecardsToSave.push({ earnedDate: day, employeeId, timecards: dailyTimecards } as SaveTimecardInput);
        }
      }
      result = await DataMutations.saveEmployeeTimecards(timecardsToSave);
    }

    const multiple = shiftData.length > 1;
    if (result) {
      notifications.success(`Shift${multiple ? 's' : ''} Duplicated ${count} Times`);
      return true;
    }

    notifications.error('Shift Duplication Failed');
    return false;
  };

  deleteJobModeShifts = async (jobInfo: JobInfoInput[]) => {
    const isMultiple = this.getJobInfoIsMultiple(jobInfo);
    const shiftWord = isMultiple ? 'Shifts' : 'Shift(s)';
    const result = await DataMutations.deleteJobTimecards(jobInfo);
    if (result) {
      notifications.success(`${shiftWord} Deleted`);
    } else {
      notifications.error(`${shiftWord} Deletion Failed`);
    }
  };

  deleteEmployeeModeShifts = async (employeeModeJobInfo: EmployeeModeJobInfo[]) => {
    const isMultiple = this.getEmployeeModeJobInfoIsMultiple(employeeModeJobInfo);
    const shiftWord = isMultiple ? 'Shifts' : 'Shift(s)';
    const saveTimecardInputs = [] as SaveTimecardInput[];
    for (const info of employeeModeJobInfo) {
      saveTimecardInputs.push(...info.dates.map((d) => ({ employeeId: info.employeeId, earnedDate: d, timecards: [] })));
    }
    const result = await DataMutations.saveEmployeeTimecards(saveTimecardInputs);
    if (result) {
      notifications.success(`${shiftWord} Deleted`);
    } else {
      notifications.error(`${shiftWord} Deletion Failed`);
    }
  };

  resetShiftsToDefault = async (jobInfo: JobInfoInput[]) => {
    const isMultiple = this.getJobInfoIsMultiple(jobInfo);
    const shiftWord = isMultiple ? 'Shifts' : 'Shift(s)';
    const result = await DataQueries.getRegenerateJobSchedule(jobInfo);
    if (result) {
      notifications.success(`${shiftWord} Reset to Default`);
    } else {
      notifications.error(`${shiftWord} Reset Failed`);
    }
  };

  getBreaksForAbsences(absenceDetails: ITimecard[], timecardTemplates?: ITimecardTemplate[]) {
    const templateBreaks = timecardTemplates?.filter((tt) => tt.reason?.category === 'break');
    if (!templateBreaks?.length || !absenceDetails.length) {
      return [];
    }

    const breakDetailsToReturn = [] as ITimecard[];
    for (const detailsGroup of Object.values(LQ(absenceDetails).groupBy('shift')) as ITimecard[][]) {
      const gapDetails = getGapDetails(detailsGroup);
      for (const gd of gapDetails) {
        const { start, end } = timestampRangeToNumericTime(gd.effective, gd.earnedDate);
        const matchingTemplateBreak = templateBreaks.find((tb) => tb.start === start && tb.end === end);
        if (matchingTemplateBreak) {
          const newBreak = timecardTemplateToTimecard({
            timecardTemplate: matchingTemplateBreak,
            template: gd.template,
            earnedDate: gd.earnedDate,
            group: gd.group,
            job: gd.job,
            occupation: gd.occupation,
            employee: gd.employee,
          });
          breakDetailsToReturn.push(newBreak);
        }
      }
    }

    return breakDetailsToReturn;
  }

  undoAbsences = async (jobInfo: JobInfoInput[]) => {
    const details: ITimecard[] = [];

    for (const jobData of jobInfo) {
      const schedules = this.schedules.find((s: DeepPartial<ISchedule>) => s.id === jobData.jobId);
      if (!schedules) continue;

      for (const jDate of jobData.dates) {
        const jDateEarnedDateStartOfDay = jDate.clone().startOfDay();
        const shift = schedules?.schedule?.find(
          (shift: DeepPartial<IShift>) =>
            shift.details && shift.earnedDate && jDateEarnedDateStartOfDay.equals((shift.earnedDate as any).clone().startOfDay()),
        );
        if (!shift) continue;

        const shiftTimecardTemplates = shift.template?.template as ITimecardTemplate[] | undefined;
        const shiftDetails = shift.details!.filter((d) => !!d?.employee).map((d: any) => ({ ...d }));
        const shiftAbsenceDetails = shiftDetails.filter((detail: ITimecard) => detail.reason?.category === 'absence');
        for (const absenceDetail of shiftAbsenceDetails) {
          // undo absence details to original reason
          absenceDetail.reason = this.getMatchingTimecardTemplateReasonForAbsenceDetail(absenceDetail, shiftTimecardTemplates);
        }
        const breakDetails = this.getBreaksForAbsences(shiftAbsenceDetails, shiftTimecardTemplates);
        details.push(...shiftDetails, ...breakDetails);
      }
    }

    const isMultiple = this.getJobInfoIsMultiple(jobInfo);
    const shiftWord = isMultiple ? 'Shifts' : 'Shift(s)';
    const timecardInputs = details.map((detail: ITimecard) => timecardToTimecardInput(detail));
    const result = await DataMutations.saveJobTimecards(timecardInputs);
    if (result) {
      notifications.success(`${shiftWord} Absences Undone`);
    } else {
      notifications.error(`${shiftWord} Undo Absences Failed`);
    }
  };

  getJobInfo(cells?: ICell[]): JobInfoInput[] {
    const jobInfo = [] as JobInfoInput[];
    const jobDatesMap = new Map<string, UTC[]>();
    cells?.forEach((cell: ICell) => {
      const jobId = this.getJobFromCell(cell).id;
      let datesArr = jobDatesMap.get(jobId);
      if (!datesArr) {
        jobDatesMap.set(jobId, []);
        datesArr = jobDatesMap.get(jobId) as UTC[];
      }
      const earnedDate = cell.value ? cell.value.earnedDate : this.getShiftCellEarnedDate(cell);
      datesArr.push(earnedDate);
    });
    jobDatesMap.forEach((dates: UTC[], jobId: string) => {
      jobInfo.push({
        jobId: jobId,
        dates: dates,
      });
    });

    return jobInfo;
  }

  getJobInfoIsMultiple(jobInfo: JobInfoInput[]): boolean {
    return jobInfo.length > 1 || jobInfo.some((j) => j.dates.length > 1);
  }

  getEmployeeModeJobInfo(cells?: ICell[]): EmployeeModeJobInfo[] {
    const employeeModeJobInfo = [] as EmployeeModeJobInfo[];
    const employeeDatesMap = new Map<string, UTC[]>();
    cells?.forEach((cell: ICell) => {
      const employeeId = this.getEmployeeFromCell(cell)!.id;
      const jobId = this.getJobFromCell(cell).id;
      const mapKey = `${employeeId}::${jobId}`;
      let datesArr = employeeDatesMap.get(mapKey);
      if (!datesArr) {
        employeeDatesMap.set(mapKey, []);
        datesArr = employeeDatesMap.get(mapKey) as UTC[];
      }
      const earnedDate = cell.value ? cell.value.earnedDate : this.getShiftCellEarnedDate(cell);
      datesArr.push(earnedDate);
    });
    employeeDatesMap.forEach((dates: UTC[], mapKey: string) => {
      const ids = mapKey.split('::');
      const employeeId = ids[0];
      const jobId = ids[1];
      employeeModeJobInfo.push({
        employeeId: employeeId,
        jobId: jobId,
        dates: dates,
      });
    });

    return employeeModeJobInfo;
  }

  getEmployeeModeJobInfoIsMultiple(employeeModeJobInfo: EmployeeModeJobInfo[]): boolean {
    return employeeModeJobInfo.length > 1 || employeeModeJobInfo.some((e) => e.dates.length > 1);
  }

  dragAndDropEmployee = async (
    dropEffect: 'move' | 'copy',
    sourceTimeEntries: ITimeEntry[],
    sourceTimeEntry: ITimeEntry,
    targetTimeEntries: ITimeEntry[],
    targetTimeEntry: ITimeEntry,
  ) => {
    const removeSourceEmployee = !!sourceTimeEntry.employee && dropEffect === 'move';
    const sourceTimecardInputs: TimecardInput[] = [];
    if (removeSourceEmployee) {
      const sourceTimeEntryTimecardIds = sourceTimeEntry.details.map((detail: ITimecard) => detail.id);
      for (const timeEntry of sourceTimeEntries) {
        const sourceTimecards = timeEntry.details;
        for (const sourceTimecard of sourceTimecards) {
          const sourceTimecardInput = timecardToTimecardInput(sourceTimecard);
          if (sourceTimeEntryTimecardIds.some((id: string) => id === sourceTimecardInput.id)) {
            sourceTimecardInput.employeeId = undefined;
          }
          sourceTimecardInputs.push(sourceTimecardInput);
        }
      }
    }

    let targetTimecardInputs: TimecardInput[] = [];
    const targetTimeEntryTimecardIds = targetTimeEntry.details.map((detail: ITimecard) => detail.id);
    const sameShift =
      sourceTimeEntry.jobId === targetTimeEntry.jobId && sourceTimeEntry.earnedDate.equals(targetTimeEntry.earnedDate);
    if (removeSourceEmployee && sameShift) {
      // dropping on same shift
      targetTimecardInputs = sourceTimecardInputs;
      for (const targetTimecardInput of targetTimecardInputs) {
        if (targetTimeEntryTimecardIds.some((id: string) => id === targetTimecardInput.id)) {
          targetTimecardInput.employeeId = sourceTimeEntry.employee?.id;
        }
      }
    } else {
      for (const timeEntry of targetTimeEntries) {
        const targetTimecards = timeEntry.details;
        for (const targetTimecard of targetTimecards) {
          const targetTimecardInput = timecardToTimecardInput(targetTimecard);
          if (targetTimeEntryTimecardIds.some((id: string) => id === targetTimecardInput.id)) {
            targetTimecardInput.employeeId = sourceTimeEntry.employee?.id;
          }
          targetTimecardInputs.push(targetTimecardInput);
        }
      }
    }

    if (removeSourceEmployee && !sameShift) {
      await DataMutations.saveJobTimecards(sourceTimecardInputs);
    }
    const targetResult = await DataMutations.saveJobTimecards(targetTimecardInputs);
    if (targetResult) {
      const sourceEmployeeName = sourceTimeEntry.text.employee;
      const targetEmployeeName = targetTimeEntry.text.employee;
      const operation = dropEffect === 'move' ? 'Moved' : 'Copied';
      notifications.success(
        `${sourceEmployeeName ? `Employee ${sourceEmployeeName} ${operation}` : ''}${
          targetEmployeeName ? `${sourceEmployeeName ? '. ' : ''}Employee ${targetEmployeeName} has been unscheduled` : ''
        }`,
      );
    } else {
      notifications.error(`Employee ${dropEffect === 'move' ? 'Move' : 'Copy'} Failed`);
    }
  };

  *getShifts(): IterableIterator<Required<IShift>> {
    for (const cell of this.selectedCells) {
      const shift = cell.value as IShift;
      if (shift?.details) {
        yield shift as Required<IShift>;
      }
    }
  }

  *getSelectedCells(): IterableIterator<[job: ScheduleJobCellData, earnedDate: UTC, shift: Required<IShift>]> {
    for (const cell of this.selectedCells) {
      const job = cell?.row?.data?.job as ScheduleJobCellData | undefined;
      const shift = cell.value as IShift;
      if (shift?.details) {
        yield [job as ScheduleJobCellData, this.getShiftCellEarnedDate(cell), shift as Required<IShift>];
      } else {
        yield [job as ScheduleJobCellData, this.getShiftCellEarnedDate(cell), shift as Required<IShift>];
      }
    }
  }

  getJobShiftsByEmployeeForSwap(): Map<string, { employee: IEmployee; timecards: ITimecard[] }> | undefined {
    const shiftDetails = LQ(this.getShifts())
      .flatMap((s) => s.details)
      .filter((x) => !!x.job?.id && x.shift === 0 && x.reason?.category === 'worked')
      .toArray();

    const jobShifts = new Map<string, { employee: IEmployee; timecards: ITimecard[] }>();
    for (const shift of shiftDetails) {
      const shifts = jobShifts.get(shift.job!.id) ?? { employee: shift.employee!, timecards: [] };
      shifts.timecards.push(shift);
      if (!shifts.employee) {
        shifts.employee = shift.employee!;
      }

      if (shifts.employee && shifts.employee.id !== shift.employee?.id) {
        return undefined;
      }

      jobShifts.set(shift.job!.id, shifts);
      if (jobShifts.keys.length > 2) {
        return undefined;
      }
    }

    if (jobShifts.size !== 2) {
      return undefined;
    }

    return jobShifts;
  }

  canSwapShiftEmployees(): boolean {
    if (this.viewOnly || scheduleModel.viewMode !== 'Job') {
      return false;
    }

    const jobShifts = this.getJobShiftsByEmployeeForSwap();
    return !!jobShifts;
  }

  debug(timecard: ITimecard) {
    console.log(
      `
Employee: ${timecard.employee?.name}
Date: ${timecard.earnedDate.toDateString()}
Effective: ${timecard.effective!.toTimeString()}
Units: ${timecard.units}
Occupation: ${timecard.occupation?.name}
Reason: ${timecard.reason?.name}
Template: ${timecard.template?.name}
Fields: ${JSON.stringify(timecard.fields ?? {}, null, 2)}
`,
    );
  }
  async swapShiftEmployees() {
    const jobShifts = this.getJobShiftsByEmployeeForSwap();
    if (!jobShifts) {
      return;
    }

    const shifts = LQ(this.getShifts())
      .flatMap((s) => s.details)
      .filter((s) => s.shift > 0)
      .toArray();

    const jobIds = Array.from(jobShifts.keys());
    const job1 = jobShifts.get(jobIds[0])!;
    const job2 = jobShifts.get(jobIds[1])!;
    // console.log('Job 1 Employee:', job1.employee.name);
    // job1.timecards.forEach((tc) => {
    //   this.debug(tc);
    // });
    // console.log('Job 2 Employee:', job2.employee.name);
    // job2.timecards.forEach((tc) => {
    //   this.debug(tc);
    // });
    const swapEmployee1 = job1.employee;
    const swapEmployee2 = job2.employee;

    const timecardInputs: TimecardInput[] = [];
    for (const timecard of job1.timecards) {
      timecard.employee = swapEmployee2;
      timecardInputs.push(timecardToTimecardInput(timecard));
    }

    for (const timecard of job2.timecards) {
      timecard.employee = swapEmployee1;
      timecardInputs.push(timecardToTimecardInput(timecard));
    }

    timecardInputs.push(...shifts.map((s) => timecardToTimecardInput(s)));

    const result = await DataMutations.saveJobTimecards(timecardInputs);
    if (result) {
      notifications.success('Employees Swapped Successfully');
    } else {
      notifications.error('Employee Swap Failed');
    }
  }

  toDisplayDateString(utc?: UTC): string {
    if (!utc) return '';
    const date = new Date(utc.utc);
    const dayShort = date.toLocaleDateString('en-us', { weekday: 'short', timeZone: 'UTC' });
    const dayNum = date.getUTCDate();
    return `${dayNum} ${dayShort}`;
  }

  removeCellRow(cell: ICell) {
    const selectedCell = scheduleInfoPanelModel.selectedCell;
    if (selectedCell === cell) {
      cell.grid.removeSelectedRows();
    } else {
      cell.grid.removeRow(cell.row.id);
    }
  }

  updateJobCellsDisplay() {
    freeze(() => {
      for (const row of this.grid.rows) {
        const jobValue = row.cells['job'].value;
        if (jobValue) {
          jobValue.updateDisplay();
        }
      }
    });
  }

  updateShiftCellsDisplay() {
    freeze(() => {
      for (const row of this.grid.rows) {
        for (const columnName of Object.keys(row.cells)) {
          if (!columnName.startsWith('shift')) {
            return;
          }
          const shiftValue = row.cells[columnName].value;
          if (shiftValue) {
            shiftValue.updateDisplay();
          }
        }
      }
    });
  }

  confirmDeleteJobs(jobIds: string[]) {
    const isMultiple = jobIds.length > 1;
    confirmationModel.load(`Are you sure you want to delete ${isMultiple ? 'these jobs' : 'this job'}?`, {
      yesAction: () => {
        this.deleteJobs(jobIds);
        return true;
      },
      maxWidth: 'sm',
      title: `Confirm Job${isMultiple ? 's' : ''} Deletion`,
    });
  }

  confirmDeleteJobModeShifts(jobInfo: JobInfoInput[]) {
    const isMultiple = this.getJobInfoIsMultiple(jobInfo);
    confirmationModel.load(`Are you sure you want to delete ${isMultiple ? 'these shifts' : 'the shift(s)'}?`, {
      yesAction: () => {
        this.deleteJobModeShifts(jobInfo);
        return true;
      },
      maxWidth: 'sm',
      title: `Confirm Shift${isMultiple ? 's' : '(s)'} Deletion`,
    });
  }

  confirmDeleteEmployeeModeShifts(employeeModeJobInfo: EmployeeModeJobInfo[]) {
    const isMultiple = this.getEmployeeModeJobInfoIsMultiple(employeeModeJobInfo);
    confirmationModel.load(`Are you sure you want to delete ${isMultiple ? 'these shifts' : 'the shift(s)'}?`, {
      yesAction: () => {
        this.deleteEmployeeModeShifts(employeeModeJobInfo);
        return true;
      },
      maxWidth: 'sm',
      title: `Confirm Shift${isMultiple ? 's' : '(s)'} Deletion`,
    });
  }

  confirmResetShiftsToDefault(jobInfo: JobInfoInput[]) {
    const isMultiple = this.getJobInfoIsMultiple(jobInfo);
    confirmationModel.load(`Are you sure you want to reset ${isMultiple ? 'these shifts' : 'the shift(s)'} to default?`, {
      yesAction: () => {
        this.resetShiftsToDefault(jobInfo);
        return true;
      },
      maxWidth: 'sm',
      title: `Confirm Shift${isMultiple ? 's' : '(s)'} Reset to Default`,
    });
  }

  confirmUndoAbsences(jobInfo: JobInfoInput[]) {
    const isMultiple = this.getJobInfoIsMultiple(jobInfo);
    confirmationModel.load(`Are you sure you want to undo the absences for ${isMultiple ? 'these shifts' : 'the shift(s)'} ?`, {
      yesAction: () => {
        this.undoAbsences(jobInfo);
        return true;
      },
      maxWidth: 'sm',
      title: `Confirm Shift${isMultiple ? 's' : '(s)'} Undo Absences}`,
    });
  }

  confirmUnassignEmployeeFromJob(job: IJob) {
    confirmationModel.load('Are you sure you want to unassign this employee from this job?', {
      yesAction: () => {
        this.unassignEmployeeFromJob(job);
        return true;
      },
      maxWidth: 'sm',
      title: 'Confirm Employee Unassignment',
    });
  }

  canSelectCellFn = (cell: ICell) => {
    const grid = cell.grid;
    if (!grid.selectedCells.length || !grid.startCell) {
      return true;
    }
    const cellToSelectType = this.getCellType(cell);
    if (cellToSelectType !== 'Shift' && cellToSelectType !== 'Job') {
      return false;
    }
    return (
      (cellToSelectType === 'Shift' && grid.selectedCells.every((c: ICell) => this.getCellType(c) === 'Shift')) ||
      (cellToSelectType === 'Job' && grid.selectedCells.every((c: ICell) => this.getCellType(c) === 'Job'))
    );
  };

  getShiftColumnHeaderTooltipFn = (columnPosition: number) => () => {
    const isPosted = this.getColumnIsPostedByPosition(columnPosition);
    const dateString = this.toDisplayDateString(mainDateRangeNavModel.startDate.add(columnPosition - 2));
    const postedDateString = mainPageModel.postedDate ? `Posted Up To ${YMDFormat(mainPageModel.postedDate)}` : 'No Posted Date';

    return `${dateString} (${isPosted ? 'Posted' : 'Unposted'})\n\n${postedDateString}`;
  };

  createGrid() {
    const cols: IColumn[] = observable([]);
    const gridGroupBy: string[] = [];
    const jobWidth = '145px';
    const employeeWidth = '135px';
    const dateWidth = '126px';

    cols.push(
      createColumn('job', `${this.viewMode === 'Employee' ? 'Assigned' : ''} Job`, {
        type: 'none',
        width: jobWidth,
        align: 'left',
        fixed: true,
        visible: this.viewMode === 'Job' || this.gridOptions.jobColumn,
        canSort: false,
        renderer: ScheduleJobCell,
        compare: ScheduleJobCellData.compare,
      }),
    );
    cols.push(
      createColumn('employee', `${this.viewMode === 'Job' ? 'Assigned' : ''} Employee`, {
        type: 'none',
        width: employeeWidth,
        align: 'left',
        fixed: true,
        canSort: false,
        cls: 'scheduleEmployeeCell',
        visible: this.viewMode === 'Employee' || this.gridOptions.assignedEmployeeColumn,
        map: nameObjMap,
        compare: nameObjCompare,
        renderer: ScheduleEmployeeCell,
      }),
    );
    for (let col = 2; col < mainDateRangeNavModel.numOfDays + 2; col++) {
      cols.push(
        createColumn(`shift${this.getShiftIdx(col)}`, this.toDisplayDateString(this.getShiftColumnEarnedDateByPosition(col)), {
          type: 'none',
          width: dateWidth,
          align: 'center',
          canSort: false,
          verticalAlign: 'top',
          tooltip: this.getShiftColumnHeaderTooltipFn(col),
          headerRenderer: ScheduleShiftColumnHeader,
          renderer: ScheduleShiftCell,
          compare: ScheduleShiftCellData.compare,
        }),
      );
    }

    if (this.viewMode === 'Employee') {
      const jobColumn = cols[0];
      cols[0] = cols[1];
      cols[1] = jobColumn;
    }

    for (const group of this.routeManager.groups || []) {
      gridGroupBy.push(group);
      if (cols.some((col: IColumn) => col.name === group)) continue;
      cols.push(
        createColumn(`${group}`, capitalize(group), {
          type: 'none',
          canSort: false,
          map: nameObjMap,
          compare: nameObjCompare,
        }),
      );
    }

    if (this.grid?.columns?.length) {
      for (const column of this.grid.columns) {
        const columnIdx = cols.findIndex((col: IColumn) => col.name === column.name);
        if (columnIdx >= 0) {
          cols[columnIdx].visible = column.visible;
        }
      }
    }

    const gridVariableKeybinds = {
      'Ctrl+Insert': (evt: React.KeyboardEvent<any>, _cell: ICell) => {
        if (!mainPageModel.canAddJobs) return;
        this.addJob();
        evt.stopPropagation();
        evt.preventDefault();
      },
      'Ctrl+D': (evt: React.KeyboardEvent<any>, _cell: ICell) => {
        if (!mainPageModel.canAddJobs) return;
        const jobs = this.getJobsFromCells(this.selectedCells);
        if (!jobs?.length) {
          return;
        }
        if (jobs.length === 1) {
          duplicateJobDialogModel.load(jobs[0]);
        } else {
          duplicateJobsDialogModel.load(jobs, this.routeManager.effective?.start ?? UTC.now());
        }
        evt.stopPropagation();
        evt.preventDefault();
      },
      'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {
        // if (!mainPageModel.canDeleteJobs) return;
        // const jobCell = this.getJobFromCell(cell);
        // if (jobCell.value) {
        //   this.confirmDeleteJob(jobCell.value.id);
        // } else {
        //   this.deleteJob();
        // }
        // evt.stopPropagation();
        // evt.preventDefault();
      },
      Delete: (evt: React.KeyboardEvent<any>, cell: ICell) => {
        if (this.viewOnly) return;
        const cellType = this.getCellType(cell);
        if (cellType === 'Job') {
          // if (cell.value) {
          //   this.confirmDeleteJob(cell.value.id);
          // } else {
          //   this.deleteJob();
          // }
        } else if (cellType === 'Employee' && cell.value) {
          // const jobCell = this.getJobFromCell(cell);
          // this.confirmUnassignEmployeeFromJob(jobCell.value);
        } else if (cellType === 'Shift' && cell.value) {
          if (this.viewMode === 'Job') {
            const jobInfo = this.getJobInfo(this.selectedCells);
            this.confirmDeleteJobModeShifts(jobInfo);
          } else {
            const employeeModeJobInfo = this.getEmployeeModeJobInfo(this.selectedCells);
            this.confirmDeleteEmployeeModeShifts(employeeModeJobInfo);
          }
        }
        evt.stopPropagation();
        evt.preventDefault();
      },
      'Ctrl+Z': (evt: React.KeyboardEvent<any>, cell: ICell) => {
        if (this.viewOnly) return;
        const cellType = this.getCellType(cell);
        if (cellType === 'Job') {
        } else if (cellType === 'Employee') {
        } else if (cellType === 'Shift') {
          this.swapShiftEmployees();
        }
        evt.stopPropagation();
        evt.preventDefault();
      },
      'Ctrl+X': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {
        // remove cut functionality
      },
      'Ctrl+V': async (evt: React.KeyboardEvent<any>, _cell: ICell) => {
        if (this.viewOnly) return;
        evt.stopPropagation();
        evt.preventDefault();
        const clipboard = (this.grid as any).clipboard;
        if (clipboard.length <= 0) {
          return;
        }
        const selectedCells = this.selectedCells.filter((selectedCell) => !selectedCell.readOnly);
        if (selectedCells.length <= 0) {
          return;
        }
        const shiftsToSave: any[] = [];
        selectedCells.forEach((selectedCell, idx) => {
          const clipboardValue = clipboard?.[idx % clipboard.length]?.value;
          const cellType = this.getCellType(selectedCell);
          const job = this.getJobFromCell(selectedCell);
          const employee = this.getEmployeeFromCell(selectedCell);
          if (
            cellType === 'Shift' &&
            job &&
            clipboardValue &&
            Object.getPrototypeOf(clipboardValue) === ScheduleShiftCellData.prototype
          ) {
            const earnedDate = this.getShiftCellEarnedDate(selectedCell);
            const newShiftData = {
              jobId: job.id,
              occupationId: job.occupation.id,
              employeeId: employee?.id,
              groupId: job.group.id,
              earnedDate: earnedDate,
              details: clipboardValue.details || [],
            };
            shiftsToSave.push(newShiftData);
          }
        });
        await this.pasteToShifts(shiftsToSave);
        (this.grid as any).clipboard = clipboard; // why?
      },
    };

    const rows = this.createGridRows(this.schedules, cols);

    const applyGridOptions = () => {
      setTimeout(() => {
        this.routeManager.navigateTo({ scheduleGridOptions: this.gridOptions });
      }, 0);
    };

    const handleShiftCellReasonsToggle = (_item: IToggleMenuItem) => {
      this.state.gridOptions.shiftCellReasons = !this.gridOptions.shiftCellReasons;
      if (!this.gridOptions.shiftCellReasons) {
        this.state.gridOptions.shiftCellBreaks = false;
      }
      applyGridOptions();
      // this.updateShiftCellsDisplay();
    };
    const handleShiftCellTimeOffRequestsToggle = (_item: IToggleMenuItem) => {
      this.state.gridOptions.shiftCellTimeOffRequests = !this.gridOptions.shiftCellTimeOffRequests;
      applyGridOptions();
      // this.updateShiftCellsDisplay();
    };
    const handleShiftCellBreaksToggle = (_item: IToggleMenuItem) => {
      this.state.gridOptions.shiftCellBreaks = !this.gridOptions.shiftCellBreaks;
      if (this.gridOptions.shiftCellBreaks) {
        this.state.gridOptions.shiftCellReasons = true;
      }
      applyGridOptions();
      // this.updateShiftCellsDisplay();
    };
    const handleShiftCellExceptionsOnlyToggle = (_item: IToggleMenuItem) => {
      this.state.gridOptions.shiftCellExceptionsOnly = !this.gridOptions.shiftCellExceptionsOnly;
      if (this.gridOptions.shiftCellExceptionsOnly) {
        this.state.gridOptions.shiftCellReasons = true;
      }
      applyGridOptions();
      // this.updateShiftCellsDisplay();
    };
    const gridToggleMenuItems: IToggleMenuItem[] = observable([
      {
        name: 'jobCellGenerator',
        title: 'Shift Pattern',
        subheader: 'Job Display Options',
        active: () => this.gridOptions.jobCellGenerator,
        onClick: (_item: IToggleMenuItem) => {
          this.state.gridOptions.jobCellGenerator = !this.gridOptions.jobCellGenerator;
          // this.updateJobCellsDisplay();
          applyGridOptions();
        },
      },
      {
        name: 'shiftCellTemplate',
        title: 'Shift Types',
        subheader: 'Shift Display Options',
        active: () => this.gridOptions.shiftCellTemplate,
        onClick: (_item: IToggleMenuItem) => {
          this.state.gridOptions.shiftCellTemplate = !this.gridOptions.shiftCellTemplate;
          // this.updateShiftCellsDisplay();
          applyGridOptions();
        },
      },
      {
        name: 'shiftCellOccupations',
        title: 'Occupations',
        active: () => this.gridOptions.shiftCellOccupations,
        visible: this.viewMode === 'Employee',
        onClick: (_item: IToggleMenuItem) => {
          this.state.gridOptions.shiftCellOccupations = !this.gridOptions.shiftCellOccupations;
          // this.updateShiftCellsDisplay();
          applyGridOptions();
        },
      },
      {
        name: 'shiftCellTimes',
        title: 'Times',
        active: () => this.gridOptions.shiftCellTimes,
        onClick: (_item: IToggleMenuItem) => {
          this.state.gridOptions.shiftCellTimes = !this.gridOptions.shiftCellTimes;
          // this.updateShiftCellsDisplay();
          applyGridOptions();
        },
      },
      {
        name: 'shiftCellReasons',
        title: 'Reasons',
        active: () => this.gridOptions.shiftCellReasons,
        onClick: handleShiftCellReasonsToggle,
      },
      {
        name: 'shiftCellBreaks',
        title: 'Breaks',
        active: () => this.gridOptions.shiftCellBreaks,
        onClick: handleShiftCellBreaksToggle,
      },
      {
        name: 'shiftCellTimeOffRequests',
        title: 'Time Off Requests',
        active: () => this.gridOptions.shiftCellTimeOffRequests,
        onClick: handleShiftCellTimeOffRequestsToggle,
      },
      {
        name: 'shiftCellExceptionsOnly',
        title: 'Exceptions Only',
        active: () => this.gridOptions.shiftCellExceptionsOnly,
        onClick: handleShiftCellExceptionsOnlyToggle,
      },
    ]);

    // create the grid model
    const gridOptionsMenuToggleColumns = this.viewMode === 'Job' ? ['employee'] : ['job'];
    const gridOptions: IGridOptions = {
      optionsMenuFn: () => ({
        items: [...createColumnsToggleMenuItems(cols, gridOptionsMenuToggleColumns), ...gridToggleMenuItems],
      }),
      clearSelectionOnBlur: false,
      isReorderable: this.gridIsReorderable,
      reorderableCellRenderer: ScheduleGridReorderableCellRenderer as any,
      variableKeybinds: gridVariableKeybinds,
      groupBy: gridGroupBy,
      multiSelect: true,
      canSelectCellFn: this.canSelectCellFn,
    };
    const grid: IGrid = createGrid(rows, cols, gridOptions);

    const colNameToWatch = this.viewMode === 'Job' ? 'employee' : 'job';
    const watchColumn = grid.column(colNameToWatch)!;
    this.watchColumnVisibility(
      () => watchColumn.visible,
      () => {
        if (this.viewMode === 'Job') {
          this.gridOptions.assignedEmployeeColumn = watchColumn.visible;
        } else {
          this.gridOptions.jobColumn = watchColumn.visible;
        }
        applyGridOptions();
      },
    );

    return grid;
  }

  createGridRows(scheduleData: DeepPartial<ISchedule>[], cols: IColumn[]): IRowData[] {
    const rows: IRowData[] = [];
    const employeeUdfNames = udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']);
    for (let row = 0; row < scheduleData.length; row++) {
      const currRow = scheduleData[row];
      const jobId = currRow.id!;
      const occupationId = currRow.occupation!.id!;
      let scheduleIdx = 0;
      const r: IRowData = { id: `${currRow.id}`, data: {} };
      for (let column = 0; column < cols.length; column++) {
        const fieldName: string = cols[column].name;
        const isEmployeeUdfCol = employeeUdfNames.includes(fieldName);
        let v: any;
        if (fieldName === 'job') {
          v = new ScheduleJobCellData(currRow as IJob);
        } else if (fieldName === 'area') {
          v = scheduleData[row]['group']?.area;
        } else if (fieldName.includes('shift')) {
          const columnDate = this.getShiftColumnEarnedDateByPosition(column);
          let shift = currRow.schedule?.[scheduleIdx] as IShift | undefined;
          shift = shift && columnDate.equals(shift.earnedDate) ? shift : undefined;
          v = shift?.details?.length ? new ScheduleShiftCellData(jobId, occupationId, shift as IShift) : undefined;
          if (shift) {
            scheduleIdx++;
          }
        } else if (isEmployeeUdfCol) {
          v = getUdfDisplayValue(fieldName, scheduleData[row].employee?.fields?.[fieldName]);
        } else {
          const s = scheduleData[row];
          v = s[fieldName as keyof typeof s];
        }
        r.data![fieldName] = v;
      }
      rows.push(r);
    }
    return rows;
  }
}

const scheduleModel = new ScheduleModel();
export default scheduleModel;
